import { useState, useEffect, useRef, useCallback } from 'react';
import {
  usePagedList,
  listParams,
  useStickyOptions,
  useStickyRow,
} from '../utils/pagedList';
import {
  usePipelineStateSnapshot,
  consumeLastOpenedContact,
} from '../hooks/usePipelineStateSnapshot';
import { syncPipelineBoardFromCache } from '../utils/pipelineSync';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  DashboardPage,
  TableChip,
  TABLE_CHIP_BASE,
} from '../../components/PremiumDesign';
import {
  Button,
  CustomSelect,
  CustomDateRangePicker,
  SearchInput,
} from '@/components';
import { useMobileScrollLock, useOutsideClick } from '@/common/hooks';
import {
  PlusIcon,
  GripVertical,
  FilterIcon,
  Settings,
} from '../../components/Icons';
import {
  getCRMPipelineStages,
  createCRMPipelineStage,
  updateCRMPipelineStage,
  reorderCRMPipelineStages,
  getCRMPipelineBoard,
  moveCRMContactStage,
  getCRMTags,
  getCRMCustomFields,
  updateCRMContact,
} from '../api';
import { getDisplayPhone } from '../utils/phoneUtils';
import { SourceIcon } from '../../components/SourceIcon';
import { TagChips } from '../components/TagChips';
import editIcon from '../../assets/editIcon.png';
import calendarIcon from '../../assets/Sidebar/calendar.png';
import datePickerCalendarIcon from '../../assets/Inquiry/calendar.png';
import { CRMStageModal } from '../components/CRMStageModal';
import { CRMLostReasonModal } from '../components/CRMLostReasonModal';
import { InlineEmployeeSelector } from '../components/InlineEmployeeSelector';
import { parseApiError } from '@/common/utils';
import { getCRMSourcesStats, getCompanyEmployees } from '../../api';
import { getWhatsAppFlows } from '../../whatsapp-flows/api';
import { useDelayedSkeleton } from '@/common/hooks';
import { usePermissions } from '@/common/hooks/usePermissions';
import { PERMISSIONS } from '@/common/config/permissions';
import { decodeId } from '@/common/utils/encryption';

// Fixed pixel widths shared between the list-view header labels and every
// data row's cells, so Tag/Date/Assigned/Primary Source/Amount always line
// up — the header also has a "Total Value" block the rows don't, so a
// single shared grid-template can't be used for both; fixed widths on just
// these five columns keeps them in sync regardless of that extra block.
const LIST_COL_WIDTH = {
  name: 180,
  // The header's "Total Value" text has no matching column in the data
  // rows — it gets a real, fixed-width slot here, and every row renders an
  // equally-wide invisible spacer in the same position, so Tag/Date/
  // Assigned/Primary Source/Amount always start at the same X regardless
  // of how long the currency value is (an absolutely-positioned label was
  // tried first and overlapped the stage name on large values).
  totalValue: 220,
  tag: 110,
  date: 170,
  assigned: 150,
  source: 150,
  amount: 100,
};

// Below this, columns start crushing into unreadable widths — scroll the
// row horizontally instead of shrinking it past usability.
const LIST_MIN_WIDTH =
  LIST_COL_WIDTH.name +
  LIST_COL_WIDTH.totalValue +
  LIST_COL_WIDTH.tag +
  LIST_COL_WIDTH.date +
  LIST_COL_WIDTH.assigned +
  LIST_COL_WIDTH.source +
  LIST_COL_WIDTH.amount +
  6 * 16 + // gaps between the 7 columns
  32; // row horizontal padding (px-4 both sides)

import { useFormatters } from '../../hooks/useFormatters';

function PipelineTagChip({ tag, className = 'max-w-full' }) {
  const tagObj = typeof tag === 'object' && tag !== null ? tag : { name: tag };
  return (
    <TableChip
      tone="neutral"
      className={`!py-0.5 !px-2 gap-1 normal-case shrink-0 !rounded-full ${className}`}
      style={tagObj.color ? { color: tagObj.color } : undefined}
    >
      <span
        className="w-1.5 h-1.5 rounded-full shrink-0"
        style={{ backgroundColor: tagObj.color || 'currentColor' }}
      />
      <span
        className={`truncate text-xs font-semibold ${!tagObj.color ? 'text-primary' : ''}`}
      >
        {tagObj.name}
      </span>
    </TableChip>
  );
}

function formatCardDate(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '-';

  const day = d.getDate().toString().padStart(2, '0');
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const month = months[d.getMonth()];
  const year = d.getFullYear();

  let hours = d.getHours();
  const minutes = d.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;
  const hoursStr = hours.toString().padStart(2, '0');

  return `${day} ${month} ${year}, ${hoursStr}:${minutes} ${ampm}`;
}

function StageSentinel({ onVisible, hasMore, isLoading }) {
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (!hasMore || isLoading || !sentinelRef.current) return;
    const node = sentinelRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          onVisible();
        }
      },
      { rootMargin: '160px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [onVisible, hasMore, isLoading]);

  if (!hasMore && !isLoading) return null;

  return (
    <div
      ref={sentinelRef}
      className="py-2.5 flex items-center justify-center shrink-0"
    >
      {isLoading && (
        <div className="flex items-center gap-2 py-1 px-3 bg-white/95 rounded-full border border-[#EBF4EA] shadow-sm">
          <svg
            className="animate-spin h-3.5 w-3.5 text-primary"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v8H4z"
            />
          </svg>
          <span className="text-xs font-medium text-[#868686]">
            Loading more leads...
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * Contact on GET /pipeline/board/.
 * created_at is the card date. updated_at stays for last-change sort only.
 * @typedef {{ created_at: string | null, updated_at?: string | null }} BoardContact
 */

function ContactCardBody({
  contact,
  isNewContact,
  availableTags,
  pipelineStages,
  currentStageId,
  onStageChange,
  companyId,
  canAssignEmployee = false,
  employeesData = [],
}) {
  const { formatCurrency } = useFormatters();

  const selectedStage = pipelineStages?.find(
    (s) => String(s.id) === String(currentStageId),
  );
  const stageColor = selectedStage?.color || '#3b82f6';

  return (
    <div className="flex flex-col h-full gap-2 w-full">
      <div className="flex flex-col">
        <div className="flex justify-between items-start gap-2">
          <div className="flex items-center gap-2">
            <p className="font-bold text-[#1F2241] text-base group-hover:text-primary transition-colors line-clamp-1">
              {contact.name || contact.contact?.name || 'Unknown'}
            </p>
            {isNewContact && (
              <span className="px-1.5 py-0.5 rounded text-xs font-bold bg-primary/20 text-primary border border-primary/30 whitespace-nowrap">
                New
              </span>
            )}
          </div>
          {contact.estimated_value !== null &&
            contact.estimated_value !== undefined &&
            contact.estimated_value !== '' && (
              <span className="text-xs font-bold text-[#50AB3D] bg-[#EBF4EA] border border-[#50AB3D]/30 px-2 py-0.5 rounded-md shrink-0">
                {formatCurrency(contact.estimated_value)}
              </span>
            )}
        </div>
        {contact.phone && (
          <span className="truncate text-sm text-[#868686]">
            {getDisplayPhone(contact)}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-2 min-w-0">
        <div className="flex">
          <TableChip
            tone="neutral"
            className="!py-0.5 !px-2 gap-1 normal-case shrink-0"
          >
            <img
              src={calendarIcon}
              alt=""
              className="w-3.5 h-3.5 object-contain shrink-0"
            />
            <span className="truncate">
              {contact.created_at ? formatCardDate(contact.created_at) : '—'}
            </span>
          </TableChip>
        </div>
        {Array.isArray(contact.tags) && contact.tags.length > 0 && (
          <TagChips
            tags={contact.tags.map((tag) => {
              const tagObj =
                typeof tag === 'object' && tag !== null ? tag : { name: tag };
              return (
                availableTags?.find((t) => t.name === tagObj.name) || tagObj
              );
            })}
            className="w-full"
          />
        )}
      </div>
      {(() => {
        const assignedEmployeeId =
          contact?.assigned_to_employee_id ||
          contact?.assigned_to_id ||
          contact?.assignee_id ||
          contact?.assignee?.id;

        const matchedEmployee = (employeesData || []).find(
          (emp) =>
            (assignedEmployeeId &&
              String(emp.id) === String(assignedEmployeeId)) ||
            (contact?.assigned_to_name &&
              (String(emp.id) === String(contact.assigned_to_name) ||
                String(emp.phone) === String(contact.assigned_to_name))),
        );

        const rawAssignedName =
          contact?.assigned_to_employee_name ||
          (contact?.assigned_to_name &&
          contact?.assigned_to_name !== 'Unassigned'
            ? contact?.assigned_to_name
            : null) ||
          contact?.assignee?.name;

        const resolvedEmployeeName =
          (rawAssignedName && !/^\d+$/.test(String(rawAssignedName).trim())
            ? rawAssignedName
            : null) ||
          matchedEmployee?.name ||
          matchedEmployee?.full_name ||
          (matchedEmployee
            ? [matchedEmployee.first_name, matchedEmployee.last_name]
                .filter(Boolean)
                .join(' ')
            : null) ||
          rawAssignedName ||
          '';

        return (
          <div className="mt-auto flex items-center justify-between gap-2 text-sm">
            <div className="flex items-center flex-wrap gap-2 min-w-0">
              <InlineEmployeeSelector
                variant="card"
                companyId={companyId}
                contactId={contact?.id}
                employeeId={assignedEmployeeId}
                employeeName={resolvedEmployeeName}
                disabled={!canAssignEmployee}
              />
              {Array.isArray(pipelineStages) && pipelineStages.length > 0 && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                  data-no-card-drag
                  className="shrink-0"
                >
                  <CustomSelect
                    value={currentStageId ?? ''}
                    onChange={(val) => onStageChange?.(contact, val)}
                    options={pipelineStages.map((s) => ({
                      value: s.id,
                      label:
                        typeof s?.name === 'string' ? s.name.trim() : s?.name,
                    }))}
                    align="center"
                    hideCheckIcon
                    hideChevron
                    dropdownWidth={160}
                    buttonClassName={`${TABLE_CHIP_BASE} !py-0.5 !px-2.5 !gap-0 w-fit max-w-[130px] appearance-none cursor-pointer focus:outline-none`}
                    buttonStyle={{
                      backgroundColor: `${stageColor}1A`,
                      color: stageColor,
                      borderColor: stageColor,
                    }}
                  />
                </div>
              )}
            </div>
            {Array.isArray(contact.sources) && contact.sources.length > 0 && (
              <div className="flex items-center gap-2 shrink-0">
                <SourceIcon type={contact.sources[0]} className="w-7 h-7" />
                <div className="min-w-0" />
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

export function CRMPipelinePage() {
  const { can } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();
  const { formatCurrency } = useFormatters();

  // ── State persistence ────────────────────────────────────────────────────
  // Restores filters, view mode, scroll position, and the last-opened contact
  // when the user returns from a detail page via the back button.
  const {
    initialState: pipelineSnapshot,
    saveState: savePipelineState,
    saveLastOpenedContact,
    clearScrollPosition,
    attachScrollListener,
    restoreScroll,
    consumeRestoreFlag,
    restorePending,
  } = usePipelineStateSnapshot();
  const skipBoardRefetchOnMount = useRef(restorePending).current;

  const [highlightedContactId, setHighlightedContactId] = useState(null);
  const [searchQuery, setSearchQuery] = useState(
    () => pipelineSnapshot.searchQuery,
  );
  const [debouncedSearch, setDebouncedSearch] = useState(
    () => pipelineSnapshot.debouncedSearch,
  );
  const [stageFilter, setStageFilter] = useState(
    () => pipelineSnapshot.stageFilter,
  );
  const [sourceFilter, setSourceFilter] = useState(
    () => pipelineSnapshot.sourceFilter,
  );
  const [tagFilter, setTagFilter] = useState(() => pipelineSnapshot.tagFilter);
  const [dateFrom, setDateFrom] = useState(() => pipelineSnapshot.dateFrom);
  const [dateTo, setDateTo] = useState(() => pipelineSnapshot.dateTo);
  const [valueMin, setValueMin] = useState(() => pipelineSnapshot.valueMin);
  const [valueMax, setValueMax] = useState(() => pipelineSnapshot.valueMax);
  const [debouncedValueMin, setDebouncedValueMin] = useState(
    () => pipelineSnapshot.debouncedValueMin ?? pipelineSnapshot.valueMin ?? '',
  );
  const [debouncedValueMax, setDebouncedValueMax] = useState(
    () => pipelineSnapshot.debouncedValueMax ?? pipelineSnapshot.valueMax ?? '',
  );
  const [consentFilter, setConsentFilter] = useState(
    () => pipelineSnapshot.consentFilter || '',
  );
  const [assignedFilter, setAssignedFilter] = useState(
    () => pipelineSnapshot.assignedFilter || '',
  );
  const [flowFilter, setFlowFilter] = useState(
    () => pipelineSnapshot.flowFilter || '',
  );
  const [customFieldSlug, setCustomFieldSlug] = useState(
    () => pipelineSnapshot.customFieldSlug || '',
  );
  const [customFieldValue, setCustomFieldValue] = useState(
    () => pipelineSnapshot.customFieldValue || '',
  );
  const [debouncedCustomFieldValue, setDebouncedCustomFieldValue] = useState(
    () =>
      pipelineSnapshot.debouncedCustomFieldValue ??
      pipelineSnapshot.customFieldValue ??
      '',
  );
  // Fields the user has added as an active filter chip, in the order added.
  const [activeFilterFields, setActiveFilterFields] = useState(
    () => pipelineSnapshot.activeFilterFields,
  );
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const filterMenuRef = useRef(null);

  useMobileScrollLock(isFilterMenuOpen);

  // Board view mode — initialised from the snapshot so the user's last choice
  // (grid vs list) is preserved when they navigate back.
  const [boardView, setBoardView] = useState(() => pipelineSnapshot.boardView);
  const [collapsedStages, setCollapsedStages] = useState({});
  const toggleStageCollapsed = (stageId) => {
    setCollapsedStages((prev) => ({ ...prev, [stageId]: !prev[stageId] }));
  };

  // Track whether this is the very first render so we can distinguish a
  // restored session (no scroll-reset) from a user-initiated filter change
  // (scroll-reset to top).
  const isFirstRender = useRef(true);
  // Consumed once on mount — deleted from sessionStorage immediately so that
  // a hard refresh (Ctrl+Shift+R) in the same tab never sees it again.
  const lastOpened = useRef(consumeLastOpenedContact()).current;
  const lastOpenedContactId = lastOpened?.id ?? null;
  const lastOpenedStageId = lastOpened?.stageId ?? null;
  const prevFiltersRef = useRef({
    searchQuery: pipelineSnapshot.searchQuery,
    debouncedSearch: pipelineSnapshot.debouncedSearch,
    stageFilter: pipelineSnapshot.stageFilter,
    sourceFilter: pipelineSnapshot.sourceFilter,
    tagFilter: pipelineSnapshot.tagFilter,
    dateFrom: pipelineSnapshot.dateFrom,
    dateTo: pipelineSnapshot.dateTo,
    debouncedValueMin:
      pipelineSnapshot.debouncedValueMin ?? pipelineSnapshot.valueMin ?? '',
    debouncedValueMax:
      pipelineSnapshot.debouncedValueMax ?? pipelineSnapshot.valueMax ?? '',
    consentFilter: pipelineSnapshot.consentFilter || '',
    assignedFilter: pipelineSnapshot.assignedFilter || '',
    flowFilter: pipelineSnapshot.flowFilter || '',
    customFieldSlug: pipelineSnapshot.customFieldSlug || '',
    debouncedCustomFieldValue:
      pipelineSnapshot.debouncedCustomFieldValue ??
      pipelineSnapshot.customFieldValue ??
      '',
    activeFilterFields: pipelineSnapshot.activeFilterFields,
  });

  let currentCompanyId =
    localStorage.getItem('company_id') || sessionStorage.getItem('company_id');
  if (!currentCompanyId) {
    try {
      const userStr =
        localStorage.getItem('user') || sessionStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        currentCompanyId = user?.company_id || user?.company?.id || user?.id;
      }
    } catch {
      // ignore parsing error
    }
  }

  // Fetch sources
  const { data: sourcesData } = useQuery({
    queryKey: ['crm-sources-stats', currentCompanyId],
    queryFn: () => getCRMSourcesStats(currentCompanyId),
    enabled: !!currentCompanyId,
  });
  const sourceOptions =
    sourcesData?.sources?.map((src) => ({
      value: src.source_type,
      label: src.source_type,
    })) || [];

  // Card tag picker and inline assign stay on the unpaged first response.
  // Filter dropdowns page separately so search does not shrink card lists.
  const { data: tagsData = [] } = useQuery({
    queryKey: ['crm-tags', currentCompanyId],
    queryFn: async () => {
      if (!currentCompanyId) return [];
      const res = await getCRMTags(currentCompanyId);
      return res?.results || res || [];
    },
    enabled: !!currentCompanyId,
  });

  const tagsList = usePagedList({
    queryKey: ['crm-tags', currentCompanyId, 'pipeline-filter'],
    enabled: !!currentCompanyId,
    idOf: (row) => row.id,
    queryFn: ({ page, search }) =>
      getCRMTags(currentCompanyId, listParams(page, search)),
  });
  const employeesDataQuery = useQuery({
    queryKey: ['companyEmployees', currentCompanyId, 'pipeline-cards'],
    queryFn: async () => {
      if (!currentCompanyId) return [];
      const res = await getCompanyEmployees(currentCompanyId, {
        page_size: 100,
      });
      return res?.results || res || [];
    },
    enabled: !!currentCompanyId,
  });
  const employeesData = employeesDataQuery.data || [];

  const employeesList = usePagedList({
    queryKey: ['companyEmployees', currentCompanyId, 'pipeline-filter'],
    enabled: !!currentCompanyId,
    idOf: (row) => row.id,
    queryFn: ({ page, search }) =>
      getCompanyEmployees(currentCompanyId, listParams(page, search)),
  });

  const flowsList = usePagedList({
    queryKey: [
      'whatsapp-flows',
      currentCompanyId,
      'pipeline-filter',
      'published',
    ],
    enabled: !!currentCompanyId,
    idOf: (row) => row.id,
    queryFn: ({ page, search }) =>
      getWhatsAppFlows(currentCompanyId, {
        status: 'published',
        ...listParams(page, search),
      }),
  });

  const customFieldsList = usePagedList({
    queryKey: ['crm-custom-fields', currentCompanyId, 'pipeline-filter'],
    enabled: !!currentCompanyId,
    idOf: (row) => row.slug ?? row.id,
    queryFn: ({ page, search }) =>
      getCRMCustomFields(currentCompanyId, listParams(page, search)),
  });

  const tagOptions = useStickyOptions(
    tagsList.rows.map((t) => ({
      value: t.name,
      label: t.name,
    })),
    tagFilter,
  );
  const employeeOptions = useStickyOptions(
    employeesList.rows.map((emp) => ({
      value: emp.id,
      label: emp.name || emp.full_name || emp.email || `User #${emp.id}`,
    })),
    assignedFilter,
  );
  const flowOptions = useStickyOptions(
    flowsList.rows
      .filter((flow) => !flow.is_draft && flow.status !== 'draft')
      .map((flow) => ({
        value: String(flow.id),
        label: flow.name || `Flow #${flow.id}`,
      })),
    flowFilter,
  );
  const customFieldOptions = useStickyOptions(
    customFieldsList.rows.map((f) => ({
      value: f.slug,
      label: f.name,
    })),
    customFieldSlug,
  );
  const selectedCustomField = useStickyRow(
    customFieldsList.rows,
    customFieldSlug,
    (f) => f.slug,
  );

  const buildBoardFilterParams = (overrides = {}) => {
    const customFieldsParam =
      customFieldSlug &&
      debouncedCustomFieldValue !== '' &&
      debouncedCustomFieldValue != null
        ? JSON.stringify({ [customFieldSlug]: debouncedCustomFieldValue })
        : undefined;

    return {
      search: debouncedSearch || undefined,
      stage_ids: stageFilter || undefined,
      sources: sourceFilter || undefined,
      tags: tagFilter || undefined,
      from_date: dateFrom || undefined,
      to_date: dateTo || undefined,
      min_estimated_value: debouncedValueMin || undefined,
      max_estimated_value: debouncedValueMax || undefined,
      consent_status: consentFilter || undefined,
      assigned_to_employee_id: assignedFilter
        ? Number(assignedFilter)
        : undefined,
      flow_id: flowFilter
        ? !isNaN(Number(flowFilter))
          ? Number(flowFilter)
          : flowFilter
        : undefined,
      custom_fields: customFieldsParam,
      ...overrides,
    };
  };

  const handleClearAllFilters = () => {
    setStageFilter('');
    setSourceFilter('');
    setTagFilter('');
    setDateFrom('');
    setDateTo('');
    setValueMin('');
    setValueMax('');
    setDebouncedValueMin('');
    setDebouncedValueMax('');
    setConsentFilter('');
    setAssignedFilter('');
    setFlowFilter('');
    setCustomFieldSlug('');
    setCustomFieldValue('');
    setDebouncedCustomFieldValue('');
    setActiveFilterFields([]);
  };

  const handleRemoveFilterField = (key) => {
    setActiveFilterFields((prev) => prev.filter((f) => f !== key));
    if (key === 'stage') setStageFilter('');
    if (key === 'source') setSourceFilter('');
    if (key === 'tag') setTagFilter('');
    if (key === 'createDate') {
      setDateFrom('');
      setDateTo('');
    }
    if (key === 'estimatedValue') {
      setValueMin('');
      setValueMax('');
      setDebouncedValueMin('');
      setDebouncedValueMax('');
    }
    if (key === 'consent') setConsentFilter('');
    if (key === 'assigned') setAssignedFilter('');
    if (key === 'automation') setFlowFilter('');
    if (key === 'customField') {
      setCustomFieldSlug('');
      setCustomFieldValue('');
      setDebouncedCustomFieldValue('');
    }
  };

  // ── Persist filter / view state to sessionStorage ───────────────────────
  useEffect(() => {
    savePipelineState({
      searchQuery,
      debouncedSearch,
      stageFilter,
      sourceFilter,
      tagFilter,
      dateFrom,
      dateTo,
      valueMin,
      valueMax,
      debouncedValueMin,
      debouncedValueMax,
      consentFilter,
      assignedFilter,
      flowFilter,
      customFieldSlug,
      customFieldValue,
      debouncedCustomFieldValue,
      activeFilterFields,
      boardView,
    });
  }, [
    searchQuery,
    debouncedSearch,
    stageFilter,
    sourceFilter,
    tagFilter,
    dateFrom,
    dateTo,
    valueMin,
    valueMax,
    debouncedValueMin,
    debouncedValueMax,
    consentFilter,
    assignedFilter,
    flowFilter,
    customFieldSlug,
    customFieldValue,
    debouncedCustomFieldValue,
    activeFilterFields,
    boardView,
    savePipelineState,
  ]);

  // ── Reset scroll-to-top when the user actively changes filters/search ────
  // Skip on the very first render (that is a restored session, not a new
  // filter action — we want to keep the saved scroll position then).
  useEffect(() => {
    if (isFirstRender.current) return;
    const prev = prevFiltersRef.current;
    const filtersChanged =
      prev.searchQuery !== searchQuery ||
      prev.debouncedSearch !== debouncedSearch ||
      prev.stageFilter !== stageFilter ||
      prev.sourceFilter !== sourceFilter ||
      prev.tagFilter !== tagFilter ||
      prev.dateFrom !== dateFrom ||
      prev.dateTo !== dateTo ||
      prev.debouncedValueMin !== debouncedValueMin ||
      prev.debouncedValueMax !== debouncedValueMax ||
      prev.consentFilter !== consentFilter ||
      prev.assignedFilter !== assignedFilter ||
      prev.flowFilter !== flowFilter ||
      prev.customFieldSlug !== customFieldSlug ||
      prev.debouncedCustomFieldValue !== debouncedCustomFieldValue ||
      JSON.stringify(prev.activeFilterFields) !==
        JSON.stringify(activeFilterFields);
    if (filtersChanged) {
      clearScrollPosition();
      prevFiltersRef.current = {
        searchQuery,
        debouncedSearch,
        stageFilter,
        sourceFilter,
        tagFilter,
        dateFrom,
        dateTo,
        debouncedValueMin,
        debouncedValueMax,
        consentFilter,
        assignedFilter,
        flowFilter,
        customFieldSlug,
        debouncedCustomFieldValue,
        activeFilterFields,
      };
    }
  }, [
    searchQuery,
    debouncedSearch,
    stageFilter,
    sourceFilter,
    tagFilter,
    dateFrom,
    dateTo,
    debouncedValueMin,
    debouncedValueMax,
    consentFilter,
    assignedFilter,
    flowFilter,
    customFieldSlug,
    debouncedCustomFieldValue,
    activeFilterFields,
    clearScrollPosition,
  ]);

  // ── Attach scroll-position listener on mount ─────────────────────────────
  // Saves the main (vertical) scroll position for list view.
  useEffect(() => {
    const cleanup = attachScrollListener();
    return cleanup;
  }, [attachScrollListener]);

  // ── Attach kanban horizontal scroll listener (grid view) ─────────────────
  // kanbanScrollRef is the horizontally-scrolling container that holds all
  // stage columns. It is a separate DOM node from #dashboard-scroll-container
  // so its scrollLeft must be saved independently.
  useEffect(() => {
    if (boardView !== 'grid') return;
    const el = kanbanScrollRef.current;
    if (!el) return;

    let rafId = null;
    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        savePipelineState({ kanbanScrollLeft: el.scrollLeft });
      });
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
    // kanbanScrollRef.current changes when the kanban mounts/unmounts;
    // we also re-attach when boardView changes.
  }, [boardView, savePipelineState]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Debounce typed filter values (estimated value + custom field)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValueMin(valueMin);
      setDebouncedValueMax(valueMax);
    }, 500);
    return () => clearTimeout(timer);
  }, [valueMin, valueMax]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCustomFieldValue(customFieldValue);
    }, 500);
    return () => clearTimeout(timer);
  }, [customFieldValue]);

  // Close the "Filter by" field menu on outside click
  useOutsideClick(
    filterMenuRef,
    () => setIsFilterMenuOpen(false),
    isFilterMenuOpen,
  );

  // Drag and drop states
  const [draggedContactId, setDraggedContactId] = useState(null);
  const [draggedStageId, setDraggedStageId] = useState(null);
  const [dragOverStageId, setDragOverStageId] = useState(null);

  // Custom pointer-driven card drag (tilts toward drag direction).
  // Position/rotation are applied directly to the DOM node on every move —
  // not via setState — so dragging never triggers a React re-render of the
  // board, which is what was causing the lag/jump.
  const [dragVisual, setDragVisual] = useState(null);
  const [cardDropTargetId, setCardDropTargetId] = useState(null);
  const [dragReadyContactId, setDragReadyContactId] = useState(null);
  const cardDragStateRef = useRef(null);
  const cardDragRAFRef = useRef(null);
  const cardGhostElRef = useRef(null);
  const kanbanScrollRef = useRef(null);
  const suppressClickRef = useRef(false);
  const cardLongPressTimerRef = useRef(null);

  // Auto-scroll while dragging a stage column to reorder it — mirrors the
  // edge-scroll behaviour of the card drag above, since native HTML5 drag
  // events only fire over elements with their own onDragOver, which made
  // the browser's built-in auto-scroll unreliable near the container edge.
  const stageDragPointerXRef = useRef(0);
  const stageDragPointerYRef = useRef(0);
  const stageDragRAFRef = useRef(null);
  const stageDragStateRef = useRef(null);
  const stageHeaderPointerMoveRef = useRef(() => {});
  const stageHeaderPointerUpRef = useRef(() => {});
  // Stable identities for window listeners (must match add/remove)
  const onStageHeaderPointerMove = useRef((e) => {
    stageHeaderPointerMoveRef.current(e);
  }).current;
  const onStageHeaderPointerUp = useRef((e) => {
    stageHeaderPointerUpRef.current(e);
  }).current;

  // Reorder mode toggle
  const [isReorderingMode, setIsReorderingMode] = useState(false);
  const [showFirstTimeTooltip, setShowFirstTimeTooltip] = useState(false);

  // Modal state
  const [isStageModalOpen, setIsStageModalOpen] = useState(false);
  const [stageModalData, setStageModalData] = useState(null);

  const [pendingLostMove, setPendingLostMove] = useState(null);

  // Leads and Inquiries are the same underlying contact flow — clicking a
  // card here should open the same full detail page the Leads list uses
  // (CRMContactOverviewPage, via /crm-contacts/overview), not a separate
  // side drawer.
  const handleViewContact = useCallback(
    (contact, stage) => {
      if (!contact?.id) return;
      const contactId = String(contact.id);
      const contactName = contact.name || contact.contact?.name || '';
      // Persist which contact the user is opening so we can highlight and
      // scroll to it when they press the back button and return here.
      saveLastOpenedContact(contactId, stage?.id);
      const paginationSnapshot = {};
      Object.entries(stagePaginationRef.current).forEach(([stageId, pag]) => {
        paginationSnapshot[stageId] = {
          page: pag.page,
          hasMore: pag.hasMore,
          nextUrl: pag.nextUrl ?? null,
        };
      });
      const kanbanEl = kanbanScrollRef.current;
      const dashEl = document.getElementById('dashboard-scroll-container');
      savePipelineState({
        stagePagination: paginationSnapshot,
        kanbanScrollLeft: kanbanEl?.scrollLeft || 0,
        scrollTop: dashEl?.scrollTop || 0,
      });
      try {
        sessionStorage.setItem('crm-contact-overview-id', contactId);
        sessionStorage.setItem('crm-contact-overview-name', contactName);
        window.dispatchEvent(new Event('crm-contact-overview-updated'));
      } catch {
        // ignore storage errors
      }
      // Original shareable URLs (currently hidden from address bar via state/sessionStorage)
      // const encodedContactId = encodeURIComponent(encodeId(contactId));
      // console.log('[Leads deep-link URLs] from pipeline', {
      //   contactId,
      //   raw: `${window.location.origin}/crm-contacts/overview?contact_id=${contactId}`,
      //   encoded: `${window.location.origin}/crm-contacts/overview?contact_id=${encodedContactId}`,
      //   pathOnlyRaw: `/crm-contacts/overview?contact_id=${contactId}`,
      //   pathOnlyEncoded: `/crm-contacts/overview?contact_id=${encodedContactId}`,
      // });
      navigate('/crm-contacts/overview', {
        state: {
          contactId,
          contactName,
          from: location.pathname + location.search,
        },
      });
    },
    [
      saveLastOpenedContact,
      savePipelineState,
      navigate,
      location.pathname,
      location.search,
    ],
  );

  // Keyboard and tooltip effect for reorder mode
  useEffect(() => {
    if (isReorderingMode) {
      if (!localStorage.getItem('hasSeenReorderTooltip')) {
        setShowFirstTimeTooltip(true);
        localStorage.setItem('hasSeenReorderTooltip', 'true');
        setTimeout(() => setShowFirstTimeTooltip(false), 5000);
      }

      const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
          setIsReorderingMode(false);
        }
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }

    // Abort in-flight stage drag when leaving reorder mode
    if (stageDragStateRef.current) {
      const st = stageDragStateRef.current;
      if (st?.pointerId != null && st?.captureEl) {
        try {
          st.captureEl.releasePointerCapture(st.pointerId);
        } catch {
          /* already released */
        }
      }
      window.removeEventListener('pointermove', onStageHeaderPointerMove);
      window.removeEventListener('pointerup', onStageHeaderPointerUp);
      window.removeEventListener('pointercancel', onStageHeaderPointerUp);
      stageDragStateRef.current = null;
      setDraggedStageId(null);
      setDragOverStageId(null);
      stopStageDragAutoScroll();
    }
  }, [isReorderingMode, onStageHeaderPointerMove, onStageHeaderPointerUp]);

  // Block text selection for the whole reorder session; restore when done
  useEffect(() => {
    if (!isReorderingMode) return;
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    return () => {
      document.body.style.userSelect = prevUserSelect;
      document.body.style.webkitUserSelect = '';
    };
  }, [isReorderingMode]);

  // Extra lock while a stage column is actively dragged
  useEffect(() => {
    if (!draggedStageId) return;
    window.getSelection()?.removeAllRanges();
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    return () => {
      document.body.style.userSelect = prevUserSelect || '';
      document.body.style.webkitUserSelect = '';
    };
  }, [draggedStageId]);

  const { data: stagesDataRaw, isLoading: isStagesLoading } = useQuery({
    queryKey: ['crm-pipeline-stages', currentCompanyId],
    queryFn: async () => {
      if (!currentCompanyId) return [];
      const res = await getCRMPipelineStages(currentCompanyId);
      return res?.results || res || [];
    },
    enabled: !!currentCompanyId,
  });
  const stagesData = stagesDataRaw || [];

  const {
    data: boardResponse,
    isLoading: isBoardLoading,
    isFetching: isBoardFetching,
    isPlaceholderData,
  } = useQuery({
    queryKey: [
      'crm-pipeline-board',
      currentCompanyId,
      debouncedSearch,
      stageFilter,
      sourceFilter,
      tagFilter,
      dateFrom,
      dateTo,
      debouncedValueMin,
      debouncedValueMax,
      consentFilter,
      assignedFilter,
      flowFilter,
      customFieldSlug,
      debouncedCustomFieldValue,
    ],
    queryFn: async () => {
      if (!currentCompanyId) return [];

      const params = buildBoardFilterParams();

      const res = await getCRMPipelineBoard(currentCompanyId, params);
      return res;
    },
    enabled: !!currentCompanyId,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    refetchOnMount: skipBoardRefetchOnMount ? false : true,
  });

  const boardData = Array.isArray(boardResponse)
    ? boardResponse
    : boardResponse?.results || boardResponse?.stages || [];

  // Stage-specific pagination: { [stageId]: { page: number, hasMore: boolean, isFetching: boolean, nextUrl: string | null } }
  const stagePaginationRef = useRef({});
  const [loadingStages, setLoadingStages] = useState({});
  const paginationFiltersKey = JSON.stringify({
    currentCompanyId,
    debouncedSearch,
    stageFilter,
    sourceFilter,
    tagFilter,
    dateFrom,
    dateTo,
    debouncedValueMin,
    debouncedValueMax,
    consentFilter,
    assignedFilter,
    flowFilter,
    customFieldSlug,
    debouncedCustomFieldValue,
  });
  const prevPaginationFiltersKeyRef = useRef(paginationFiltersKey);
  const paginationInitializedRef = useRef(false);

  useEffect(() => {
    const stagesList = Array.isArray(boardResponse)
      ? boardResponse
      : boardResponse?.results || boardResponse?.stages || [];
    if (!stagesList.length) return;

    const filtersChanged =
      prevPaginationFiltersKeyRef.current !== paginationFiltersKey;
    prevPaginationFiltersKeyRef.current = paginationFiltersKey;

    const shouldRestorePagination =
      !paginationInitializedRef.current &&
      restorePending &&
      pipelineSnapshot.stagePagination &&
      Object.keys(pipelineSnapshot.stagePagination).length > 0;

    const contactInCache =
      lastOpenedContactId &&
      stagesList.some((stage) => {
        const contacts = stage?.contacts || stage?.leads || stage?.items || [];
        return contacts.some(
          (c) => String(c.id) === String(lastOpenedContactId),
        );
      });

    const nextPagination = {};
    stagesList.forEach((stage) => {
      const stageContacts =
        stage?.contacts || stage?.leads || stage?.items || [];
      const totalCount =
        stage?.count != null ? stage.count : stageContacts.length;
      const initialNextUrl = boardResponse?.next || null;
      const hasMore = totalCount > stageContacts.length || !!initialNextUrl;
      const existing = stagePaginationRef.current[stage.id];
      const saved = pipelineSnapshot.stagePagination?.[stage.id];

      if (shouldRestorePagination && saved) {
        // Keep saved page only when appended cache is still present.
        // Otherwise start at page 1 so we can replay pages sequentially.
        const useSavedPage = contactInCache || !hasMore;
        nextPagination[stage.id] = {
          page: useSavedPage ? saved.page || 1 : 1,
          hasMore,
          isFetching: false,
          nextUrl: useSavedPage
            ? (saved.nextUrl ?? initialNextUrl)
            : initialNextUrl,
        };
      } else if (
        !filtersChanged &&
        existing &&
        paginationInitializedRef.current
      ) {
        nextPagination[stage.id] = {
          ...existing,
          hasMore:
            totalCount > stageContacts.length || existing.hasMore === true,
        };
      } else {
        nextPagination[stage.id] = {
          page: 1,
          hasMore,
          isFetching: false,
          nextUrl: initialNextUrl,
        };
      }
    });

    stagePaginationRef.current = nextPagination;
    paginationInitializedRef.current = true;
    if (filtersChanged) {
      setLoadingStages({});
    }
  }, [
    paginationFiltersKey,
    boardResponse,
    restorePending,
    pipelineSnapshot.stagePagination,
    lastOpenedContactId,
  ]);

  const fetchNextPageForStage = async (stage) => {
    if (!currentCompanyId || isReorderingMode) return;

    const currentPag = stagePaginationRef.current[stage.id] || {
      page: 1,
      hasMore: true,
      isFetching: false,
      nextUrl: null,
    };

    if (currentPag.isFetching || currentPag.hasMore === false) {
      return;
    }

    const nextPage = (currentPag.page || 1) + 1;

    stagePaginationRef.current[stage.id] = {
      ...currentPag,
      isFetching: true,
    };
    setLoadingStages((prev) => ({ ...prev, [stage.id]: true }));

    try {
      const baseParams = buildBoardFilterParams({
        stage_ids: stage.id,
        page: nextPage,
      });

      let res;
      if (currentPag.nextUrl) {
        try {
          const parsed = new URL(currentPag.nextUrl, window.location.origin);
          const urlParams = Object.fromEntries(parsed.searchParams.entries());
          res = await getCRMPipelineBoard(currentCompanyId, {
            ...baseParams,
            ...urlParams,
            stage_ids: stage.id,
          });
        } catch {
          res = await getCRMPipelineBoard(currentCompanyId, baseParams);
        }
      } else {
        res = await getCRMPipelineBoard(currentCompanyId, baseParams);
      }

      let newContacts = [];
      let newNextUrl = null;

      if (res) {
        newNextUrl = res.next || null;
        if (Array.isArray(res.results)) {
          const matchingStage =
            res.results.find((s) => String(s.id) === String(stage.id)) ||
            res.results[0];
          if (matchingStage && Array.isArray(matchingStage.contacts)) {
            newContacts = matchingStage.contacts;
          } else if (
            res.results[0] &&
            (res.results[0].name || res.results[0].phone || res.results[0].id)
          ) {
            newContacts = res.results;
          }
        } else if (Array.isArray(res.contacts)) {
          newContacts = res.contacts;
        } else if (Array.isArray(res)) {
          const matchingStage =
            res.find((s) => String(s.id) === String(stage.id)) || res[0];
          if (matchingStage && Array.isArray(matchingStage.contacts)) {
            newContacts = matchingStage.contacts;
          } else {
            newContacts = res;
          }
        }
      }

      let totalCountForStage = 0;

      if (newContacts && newContacts.length > 0) {
        const queryKeyPrefix = ['crm-pipeline-board', currentCompanyId];
        const previousBoards = queryClient.getQueriesData({
          queryKey: queryKeyPrefix,
        });

        previousBoards.forEach(([queryKey]) => {
          queryClient.setQueryData(queryKey, (old) => {
            if (!old) return old;
            const isArray = Array.isArray(old);
            const stagesSource = isArray
              ? old
              : old.results || old.stages || [];
            if (!stagesSource) return old;

            const newStages = stagesSource.map((s) => {
              if (String(s.id) === String(stage.id)) {
                const existingContacts = s.contacts || s.leads || s.items || [];
                const existingIds = new Set(existingContacts.map((c) => c.id));
                const uniqueNew = newContacts.filter(
                  (c) => !existingIds.has(c.id),
                );
                const updatedList = [...existingContacts, ...uniqueNew];
                totalCountForStage = updatedList.length;
                return {
                  ...s,
                  contacts: updatedList,
                };
              }
              return s;
            });

            if (isArray) {
              return newStages;
            } else {
              return { ...old, results: newStages, stages: newStages };
            }
          });
        });
      }

      const stageTotalCount = stage?.count;
      const hasMoreAfter =
        newContacts.length > 0 &&
        (newNextUrl !== null ||
          (stageTotalCount != null && totalCountForStage < stageTotalCount));

      stagePaginationRef.current[stage.id] = {
        page: nextPage,
        hasMore: hasMoreAfter,
        isFetching: false,
        nextUrl: newNextUrl,
      };
      savePipelineState({
        stagePagination: {
          [stage.id]: {
            page: nextPage,
            hasMore: hasMoreAfter,
            nextUrl: newNextUrl,
          },
        },
      });
    } catch (error) {
      console.error(`Failed to load next page for stage ${stage.id}:`, error);
      stagePaginationRef.current[stage.id] = {
        ...currentPag,
        isFetching: false,
      };
    } finally {
      setLoadingStages((prev) => ({ ...prev, [stage.id]: false }));
    }
  };

  const handleStageColumnScroll = (e, stage) => {
    if (isReorderingMode) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    savePipelineState({
      stageColumnScroll: { [stage.id]: scrollTop },
    });
    if (scrollHeight - scrollTop - clientHeight < 150) {
      fetchNextPageForStage(stage);
    }
  };

  // Handle highlight state from dashboard redirect or URL query params
  useEffect(() => {
    let leadId = null;

    if (location.state?.highlightLeadId) {
      leadId = Number(location.state.highlightLeadId);
    } else {
      const searchParams = new URLSearchParams(location.search);
      const encodedId = searchParams.get('contact_id');
      if (encodedId) {
        const decoded = decodeId(encodedId);
        if (decoded && !isNaN(Number(decoded))) {
          leadId = Number(decoded);
        } else if (!isNaN(Number(encodedId))) {
          leadId = Number(encodedId);
        }
      }
    }

    if (leadId) {
      setHighlightedContactId(leadId);

      // Notification deep-link highlight: 1 second border flash only.
      const timer = setTimeout(() => {
        setHighlightedContactId(null);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [location.state, location.search]);

  // ── Restore scroll position + highlight last-opened contact ─────────────
  // Fires once after the initial board data load completes. On subsequent
  // fetches (filter changes) this is skipped via the isFirstRender guard.
  useEffect(() => {
    if (isBoardLoading) return;

    if (isFirstRender.current) {
      isFirstRender.current = false;

      const getContactEl = (id) =>
        document.getElementById(`contact-card-${id}`) ||
        document.getElementById(`contact-card-list-${id}`);

      const restoreColumnScrolls = () => {
        const cols = pipelineSnapshot.stageColumnScroll || {};
        Object.entries(cols).forEach(([stageId, top]) => {
          const col = document.getElementById(`stage-column-scroll-${stageId}`);
          if (col && top) col.scrollTop = top;
        });
      };

      const restoreAllScrollPositions = () => {
        if (boardView === 'grid') {
          const kanbanEl = kanbanScrollRef.current;
          if (kanbanEl && pipelineSnapshot.kanbanScrollLeft) {
            kanbanEl.scrollLeft = pipelineSnapshot.kanbanScrollLeft;
          }
          restoreColumnScrolls();
        } else {
          restoreScroll();
        }
      };

      const finishRestore = (id) => {
        restoreAllScrollPositions();
        requestAnimationFrame(() => {
          const element = id != null ? getContactEl(id) : null;
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'center',
            });
          }
        });
        consumeRestoreFlag();
      };

      let cancelled = false;

      if (lastOpenedContactId) {
        const id = Number(lastOpenedContactId);
        setHighlightedContactId(id);
        const highlightTimer = setTimeout(
          () => setHighlightedContactId(null),
          1000,
        );

        const run = async () => {
          const stagesList = Array.isArray(boardData)
            ? boardData
            : boardData?.results || boardData?.stages || [];
          const stageForReplay =
            (lastOpenedStageId &&
              stagesList.find(
                (s) => String(s.id) === String(lastOpenedStageId),
              )) ||
            stagesList.find((stage) => {
              const contacts =
                stage?.contacts || stage?.leads || stage?.items || [];
              return contacts.some(
                (c) => String(c.id) === String(lastOpenedContactId),
              );
            });

          if (stageForReplay && !getContactEl(id)) {
            let attempts = 0;
            while (
              !cancelled &&
              attempts < 25 &&
              !getContactEl(id) &&
              stagePaginationRef.current[stageForReplay.id]?.hasMore
            ) {
              await fetchNextPageForStage(stageForReplay);
              await new Promise((resolve) => {
                requestAnimationFrame(() => resolve());
              });
              attempts += 1;
            }
          }

          if (cancelled) return;
          finishRestore(id);
        };

        run();
        return () => {
          cancelled = true;
          clearTimeout(highlightTimer);
        };
      }

      const scrollTimer = setTimeout(() => {
        finishRestore(null);
      }, 100);
      return () => {
        cancelled = true;
        clearTimeout(scrollTimer);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBoardLoading]);

  // Scroll a notification-highlighted card into view on subsequent navigations
  // (e.g. deep-link from dashboard). This is separate from the restore above.
  useEffect(() => {
    if (!isBoardLoading && highlightedContactId && !isFirstRender.current) {
      const timer = setTimeout(() => {
        const element =
          document.getElementById(`contact-card-${highlightedContactId}`) ||
          document.getElementById(`contact-card-list-${highlightedContactId}`);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center',
          });
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isBoardLoading, highlightedContactId]);

  const isSearchOrFilterFetching =
    isPlaceholderData && isBoardFetching && boardResponse !== undefined;
  const showDelayedSkeleton = useDelayedSkeleton(
    isSearchOrFilterFetching,
    300,
    500,
  );

  const isLoading =
    isStagesLoading ||
    isBoardLoading ||
    !boardResponse ||
    !stagesDataRaw ||
    showDelayedSkeleton;

  const queryClient = useQueryClient();

  // Synchronize and highlight contact when returning from Inquiry Overview page
  const prevPathnameRef = useRef(location.pathname);
  useEffect(() => {
    const isReturningToPipeline =
      prevPathnameRef.current === '/crm-contacts/overview' &&
      location.pathname === '/crm-pipeline';
    prevPathnameRef.current = location.pathname;

    if (isReturningToPipeline) {
      const pendingContact = consumeLastOpenedContact();
      if (pendingContact?.id) {
        syncPipelineBoardFromCache(queryClient, pendingContact.id);

        const idNum = Number(pendingContact.id);
        setHighlightedContactId(idNum);
        const timer = setTimeout(() => {
          setHighlightedContactId(null);
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [location.pathname, queryClient]);

  const handleCreateStageMutation = useMutation({
    mutationFn: (args) => {
      if (!canUpdateStages)
        throw new Error('Permission denied: Cannot create stages.');
      return createCRMPipelineStage(args);
    },
    onSuccess: () => {
      toast.success('Inquiry stage created successfully!');
      setIsStageModalOpen(false);
      queryClient.invalidateQueries({
        queryKey: ['crm-pipeline-stages', currentCompanyId],
      });
      queryClient.invalidateQueries({
        queryKey: ['crm-segment-filter-fields', currentCompanyId],
      });
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to create stage.');
    },
  });

  const handleUpdateStageMutation = useMutation({
    mutationFn: (args) => {
      if (!canUpdateStages)
        throw new Error('Permission denied: Cannot update stages.');
      return updateCRMPipelineStage(args);
    },
    onSuccess: () => {
      toast.success('Inquiry stage updated successfully!');
      setIsStageModalOpen(false);
      queryClient.invalidateQueries({
        queryKey: ['crm-pipeline-stages', currentCompanyId],
      });
      queryClient.invalidateQueries({
        queryKey: ['crm-segment-filter-fields', currentCompanyId],
      });
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to update stage.');
    },
  });

  const handleReorderStagesMutation = useMutation({
    mutationFn: (args) => {
      if (!canUpdateStages)
        throw new Error('Permission denied: Cannot reorder stages.');
      return reorderCRMPipelineStages(args);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['crm-pipeline-stages', currentCompanyId],
      });
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to reorder stages.');
    },
  });

  const handleMoveContactMutation = useMutation({
    mutationFn: (args) => {
      if (!canMoveLeads)
        throw new Error('Permission denied: Cannot move leads.');
      return moveCRMContactStage(args);
    },
    onMutate: async ({ companyId, contactId, payload }) => {
      await queryClient.cancelQueries({
        queryKey: ['crm-pipeline-board', companyId],
      });

      const queryKeyPrefix = ['crm-pipeline-board', companyId];
      const previousBoards = queryClient.getQueriesData({
        queryKey: queryKeyPrefix,
      });

      previousBoards.forEach(([queryKey]) => {
        queryClient.setQueryData(queryKey, (old) => {
          if (!old) return old;

          const isArray = Array.isArray(old);
          const stagesSource = isArray ? old : old.stages;
          if (!stagesSource) return old;

          const newStages = JSON.parse(JSON.stringify(stagesSource));
          let movedContact = null;

          for (const stage of newStages) {
            const contacts = stage.contacts || stage.leads || stage.items || [];
            const index = contacts.findIndex((c) => c.id === contactId);
            if (index !== -1) {
              movedContact = contacts.splice(index, 1)[0];
              contacts.sort((a, b) => (a.id || 0) - (b.id || 0));

              if (typeof stage.count === 'number') {
                stage.count = Math.max(0, stage.count - 1);
              }

              // Subtract estimated_value from the old stage
              const val = parseFloat(movedContact.estimated_value);
              if (!isNaN(val)) {
                stage.total_estimated_value = String(
                  (
                    parseFloat(stage.total_estimated_value || '0') - val
                  ).toFixed(2),
                );
              }
              break;
            }
          }

          if (movedContact) {
            const targetStage = newStages.find(
              (s) => String(s.id) === String(payload.target_stage_id),
            );
            if (targetStage) {
              const targetContacts =
                targetStage.contacts ||
                targetStage.leads ||
                targetStage.items ||
                [];
              targetContacts.push(movedContact);
              targetContacts.sort((a, b) => (a.id || 0) - (b.id || 0));

              if (typeof targetStage.count === 'number') {
                targetStage.count += 1;
              }

              // Add estimated_value to the new stage
              const val = parseFloat(movedContact.estimated_value);
              if (!isNaN(val)) {
                targetStage.total_estimated_value = String(
                  (
                    parseFloat(targetStage.total_estimated_value || '0') + val
                  ).toFixed(2),
                );
              }
            }
          }

          if (isArray) {
            return newStages;
          } else {
            return { ...old, results: newStages, stages: newStages };
          }
        });
      });

      return { previousBoards, contactId, companyId };
    },
    onSuccess: (data, variables) => {
      setPendingLostMove(null);
      toast.success('Lead moved successfully!');
      queryClient.invalidateQueries({
        queryKey: ['crm-pipeline-board', variables.companyId],
      });
    },
    onError: (err, variables, context) => {
      if (context?.previousBoards) {
        context.previousBoards.forEach(([queryKey, previousValue]) => {
          queryClient.setQueryData(queryKey, previousValue);
        });
      }

      const status = err?.response?.status;
      if (status >= 500) {
        toast.error('An unexpected server error occurred. Please try again.');
      } else {
        const { message } = parseApiError(err);
        toast.error(message || 'Failed to move lead.');
      }
    },
  });

  // Lightweight — the full "edit lead" flow now lives on the contact
  // detail page (CRMContactOverviewPage), this only patches a tag inline.
  const handleChangeTagMutation = useMutation({
    mutationFn: updateCRMContact,
    onSuccess: () => {
      toast.success('Tag updated!');
      queryClient.invalidateQueries({
        queryKey: ['crm-pipeline-board', currentCompanyId],
      });
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to update tag.');
    },
  });

  const handleChangeContactTag = (contactId, tagId) => {
    const tag = tagsData?.find((t) => String(t.id) === String(tagId));
    if (!tag) return;
    handleChangeTagMutation.mutate({
      companyId: currentCompanyId,
      contactId,
      payload: { tags: [tag.name], tag_ids: [tag.id] },
    });
  };

  const pipelineStages = Array.isArray(stagesData) ? stagesData : [];
  // Board already partitions contacts by stage — filtering by stage just
  // narrows which stage column(s) render, no API param needed.
  const visibleStages = stageFilter
    ? pipelineStages.filter((s) => String(s.id) === String(stageFilter))
    : pipelineStages;

  // Same move logic as drag-and-drop (handleCardPointerUp above) — a move
  // into a "lost" stage requires a reason first, everything else moves
  // straight through handleMoveContactMutation.
  const handleChangeContactStage = (contact, newStageId) => {
    const targetStage = pipelineStages.find(
      (s) => String(s.id) === String(newStageId),
    );
    if (!targetStage) return;
    if (targetStage.is_lost || targetStage.slug === 'lost') {
      setPendingLostMove({
        contactId: contact.id,
        target_stage_id: targetStage.id,
      });
      return;
    }
    handleMoveContactMutation.mutate({
      companyId: currentCompanyId,
      contactId: contact.id,
      payload: { target_stage_id: newStageId },
    });
  };

  const openEditForm = (stage) => {
    if (isReorderingMode) return;
    setStageModalData(stage);
    setIsStageModalOpen(true);
  };

  const openCreateForm = () => {
    setStageModalData(null);
    setIsStageModalOpen(true);
  };

  const handleSaveModalStage = (formData) => {
    if (stageModalData) {
      handleUpdateStageMutation.mutate({
        companyId: currentCompanyId,
        stageId: stageModalData.id,
        payload: formData,
      });
    } else {
      handleCreateStageMutation.mutate({
        companyId: currentCompanyId,
        payload: formData,
      });
    }
  };

  const handleDropColumn = (targetStageId, sourceStageId = draggedStageId) => {
    if (
      sourceStageId == null ||
      targetStageId == null ||
      String(sourceStageId) === String(targetStageId)
    ) {
      return;
    }
    const newStages = [...pipelineStages];
    const draggedIndex = newStages.findIndex(
      (s) => String(s.id) === String(sourceStageId),
    );
    const targetIndex = newStages.findIndex(
      (s) => String(s.id) === String(targetStageId),
    );
    if (draggedIndex === -1 || targetIndex === -1) return;

    const [removed] = newStages.splice(draggedIndex, 1);
    newStages.splice(targetIndex, 0, removed);

    const payload = newStages.map((s, i) => ({
      id: s.id,
      display_order: i + 1,
    }));

    handleReorderStagesMutation.mutate({
      companyId: currentCompanyId,
      payload,
    });
  };

  const runStageDragAutoScroll = () => {
    const EDGE = 90;
    const MAX_SPEED = 20;
    if (boardView === 'list') {
      const el = document.getElementById('dashboard-scroll-container');
      if (el) {
        const rect = el.getBoundingClientRect();
        const y = stageDragPointerYRef.current;
        const distTop = y - rect.top;
        const distBottom = rect.bottom - y;
        if (distTop < EDGE) {
          el.scrollTop -= MAX_SPEED * (1 - Math.max(0, distTop) / EDGE);
        } else if (distBottom < EDGE) {
          el.scrollTop += MAX_SPEED * (1 - Math.max(0, distBottom) / EDGE);
        }
      }
    } else {
      const el = kanbanScrollRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        const x = stageDragPointerXRef.current;
        const distLeft = x - rect.left;
        const distRight = rect.right - x;
        if (distLeft < EDGE) {
          el.scrollLeft -= MAX_SPEED * (1 - Math.max(0, distLeft) / EDGE);
        } else if (distRight < EDGE) {
          el.scrollLeft += MAX_SPEED * (1 - Math.max(0, distRight) / EDGE);
        }
      }
    }
    stageDragRAFRef.current = requestAnimationFrame(runStageDragAutoScroll);
  };

  const startStageDragAutoScroll = () => {
    if (stageDragRAFRef.current) return;
    stageDragRAFRef.current = requestAnimationFrame(runStageDragAutoScroll);
  };

  const stopStageDragAutoScroll = () => {
    if (stageDragRAFRef.current) {
      cancelAnimationFrame(stageDragRAFRef.current);
      stageDragRAFRef.current = null;
    }
  };

  const stopStageHeaderDrag = () => {
    const st = stageDragStateRef.current;
    if (st?.pointerId != null && st?.captureEl) {
      try {
        st.captureEl.releasePointerCapture(st.pointerId);
      } catch {
        /* already released */
      }
    }
    window.removeEventListener('pointermove', onStageHeaderPointerMove);
    window.removeEventListener('pointerup', onStageHeaderPointerUp);
    window.removeEventListener('pointercancel', onStageHeaderPointerUp);
    stageDragStateRef.current = null;
    setDraggedStageId(null);
    setDragOverStageId(null);
    stopStageDragAutoScroll();
  };

  const handleStageHeaderPointerMove = (e) => {
    const st = stageDragStateRef.current;
    if (!st) return;
    if (st.pointerId != null && e.pointerId !== st.pointerId) return;

    const dx = e.clientX - st.startClientX;
    const dy = e.clientY - st.startClientY;
    const threshold = st.pointerType === 'touch' ? 8 : 5;
    if (!st.moved && Math.abs(dx) < threshold && Math.abs(dy) < threshold)
      return;

    if (!st.moved) {
      st.moved = true;
      suppressClickRef.current = true;
      window.getSelection()?.removeAllRanges();
      setDraggedStageId(st.stageId);
      if (boardView === 'list') {
        stageDragPointerYRef.current = e.clientY;
      } else {
        stageDragPointerXRef.current = e.clientX;
      }
      startStageDragAutoScroll();
    }

    if (st.pointerType === 'touch') {
      e.preventDefault();
    }

    stageDragPointerXRef.current = e.clientX;
    stageDragPointerYRef.current = e.clientY;

    let overId = null;
    const zones = document.querySelectorAll('[data-stage-dropzone]');
    for (const zoneEl of zones) {
      const rect = zoneEl.getBoundingClientRect();
      if (
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom
      ) {
        overId = zoneEl.getAttribute('data-stage-dropzone');
        break;
      }
    }
    if (overId !== st.overStageId) {
      st.overStageId = overId;
      setDragOverStageId(overId);
    }
  };

  const handleStageHeaderPointerUp = (e) => {
    const st = stageDragStateRef.current;
    if (
      st?.pointerId != null &&
      e?.pointerId != null &&
      e.pointerId !== st.pointerId
    ) {
      return;
    }

    if (
      st?.moved &&
      st.overStageId &&
      String(st.overStageId) !== String(st.stageId)
    ) {
      handleDropColumn(st.overStageId, st.stageId);
    }

    stopStageHeaderDrag();
  };

  stageHeaderPointerMoveRef.current = handleStageHeaderPointerMove;
  stageHeaderPointerUpRef.current = handleStageHeaderPointerUp;

  const handleStageHeaderPointerDown = (e, stage) => {
    if (!isReorderingMode || e.button !== 0) return;
    // Mouse uses native HTML5 drag on the header — pointer path is for touch
    if (e.pointerType === 'mouse') return;
    // Nested controls (edit, etc.) must not start a column drag
    if (e.target.closest('button, a, input, select, textarea')) return;

    e.preventDefault();

    stageDragStateRef.current = {
      stageId: stage.id,
      startClientX: e.clientX,
      startClientY: e.clientY,
      moved: false,
      overStageId: null,
      pointerId: e.pointerId,
      pointerType: e.pointerType || 'touch',
      captureEl: e.currentTarget,
    };

    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* older browsers */
    }

    window.addEventListener('pointermove', onStageHeaderPointerMove, {
      passive: false,
    });
    window.addEventListener('pointerup', onStageHeaderPointerUp);
    window.addEventListener('pointercancel', onStageHeaderPointerUp);
  };

  const handleStageHeaderDragStart = (e, stage) => {
    if (!isReorderingMode) {
      e.preventDefault();
      return;
    }
    if (e.target.closest('button, a, input, select, textarea')) {
      e.preventDefault();
      return;
    }
    e.stopPropagation();
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('stageId', String(stage.id));
    // Transparent drag image keeps header chrome readable; browser still
    // shows the default drag feedback for the element.
    window.getSelection()?.removeAllRanges();
    setDraggedStageId(stage.id);
    if (boardView === 'list') {
      stageDragPointerYRef.current = e.clientY;
    } else {
      stageDragPointerXRef.current = e.clientX;
    }
    startStageDragAutoScroll();
  };

  const handleStageHeaderDragEnd = () => {
    setDraggedStageId(null);
    setDragOverStageId(null);
    stopStageDragAutoScroll();
  };

  const handleCardTouchMove = (e) => {
    const st = cardDragStateRef.current;
    if (st && (st.isDragReady || st.moved)) {
      if (e.cancelable) {
        e.preventDefault();
      }
    }
  };

  const handleCardContextMenu = (e) => {
    const st = cardDragStateRef.current;
    if (st && (st.isDragReady || st.moved)) {
      e.preventDefault();
    }
  };

  const stopCardDrag = () => {
    if (cardLongPressTimerRef.current) {
      clearTimeout(cardLongPressTimerRef.current);
      cardLongPressTimerRef.current = null;
    }
    const st = cardDragStateRef.current;
    if (st?.pointerId != null && st?.captureEl) {
      try {
        st.captureEl.releasePointerCapture(st.pointerId);
      } catch {
        /* already released */
      }
    }
    window.removeEventListener('pointermove', handleCardPointerMove);
    window.removeEventListener('pointerup', handleCardPointerUp);
    window.removeEventListener('pointercancel', handleCardPointerUp);
    window.removeEventListener('touchmove', handleCardTouchMove);
    window.removeEventListener('contextmenu', handleCardContextMenu);
    if (cardDragRAFRef.current) {
      cancelAnimationFrame(cardDragRAFRef.current);
      cardDragRAFRef.current = null;
    }
    cardDragStateRef.current = null;
    cardGhostElRef.current = null;
    setDragVisual(null);
    setCardDropTargetId(null);
    setDraggedContactId(null);
    setDragReadyContactId(null);
    document.body.style.userSelect = '';
    document.body.style.touchAction = '';
  };

  // Writes position + rotation straight to the DOM node, bypassing React
  // state/re-render entirely — this is what makes the drag track the
  // cursor immediately with no lag, on every single pixel of movement.
  const applyCardGhostTransform = () => {
    const st = cardDragStateRef.current;
    const el = cardGhostElRef.current;
    if (!st || !el) return;
    el.style.transform = `translate3d(${st.x}px, ${st.y}px, 0) rotate(${st.tilt}deg)`;
  };

  // Runs once per animation frame (not once per raw pointer event) so the
  // velocity used for tilt is sampled at a steady rate — raw pointermove
  // events fire at an irregular rate and produce a noisy, pulsing tilt.
  const runCardDragFrame = () => {
    const st = cardDragStateRef.current;
    if (!st || !st.moved) {
      cardDragRAFRef.current = null;
      return;
    }

    const rawDeltaX = st.x - st.frameLastX;
    st.frameLastX = st.x;

    // Raw per-frame delta from a human hand is jittery (sometimes 1px,
    // sometimes 5px between frames even at "constant" speed) — smoothing
    // the delta itself first, then easing the tilt toward it, is what
    // removes the "keeps re-adjusting" feeling. Position (translate) is
    // NOT touched here — it's applied 1:1 on every pointer event elsewhere,
    // so the card itself never lags behind the cursor, only the lean does.
    st.smoothDeltaX = st.smoothDeltaX + (rawDeltaX - st.smoothDeltaX) * 0.15;

    if (Math.abs(st.smoothDeltaX) > 0.6) {
      const targetTilt = Math.max(-8, Math.min(8, st.smoothDeltaX * 1.6));
      st.tilt = st.tilt + (targetTilt - st.tilt) * 0.15;
      applyCardGhostTransform();
    }
    // else: pointer has settled — leave st.tilt exactly as it is instead
    // of easing back toward flat.

    // Hit-testing for the drop target: compare the pointer against every
    // drop zone's bounding rect rather than `elementFromPoint`, which only
    // finds whatever's topmost at that exact pixel — over a tall list-view
    // section, hovering a row further down could resolve to some other
    // element in between and miss the zone. A rect scan means the whole
    // section counts, top to bottom. Doing it once per frame (not per raw
    // pointer event) keeps it off the hot path that position updates run
    // on.
    let overId = null;
    const zones = document.querySelectorAll('[data-stage-dropzone]');
    for (const zoneEl of zones) {
      const rect = zoneEl.getBoundingClientRect();
      if (
        st.pointerClientX >= rect.left &&
        st.pointerClientX <= rect.right &&
        st.pointerClientY >= rect.top &&
        st.pointerClientY <= rect.bottom
      ) {
        overId = zoneEl.getAttribute('data-stage-dropzone');
        break;
      }
    }
    if (overId !== st.overStageId) {
      st.overStageId = overId;
      setCardDropTargetId(overId);
    }

    // Auto-scroll when dragging near the edge of the relevant scroll
    // container — horizontal for the kanban row, vertical for the list
    // view (which scrolls at the page level).
    const EDGE = 90;
    const MAX_SPEED = 20;
    if (st.sourceView === 'list') {
      const el = document.getElementById('dashboard-scroll-container');
      if (el) {
        const rect = el.getBoundingClientRect();
        const y = st.pointerClientY;
        const distTop = y - rect.top;
        const distBottom = rect.bottom - y;
        if (distTop < EDGE) {
          el.scrollTop -= MAX_SPEED * (1 - Math.max(0, distTop) / EDGE);
        } else if (distBottom < EDGE) {
          el.scrollTop += MAX_SPEED * (1 - Math.max(0, distBottom) / EDGE);
        }
      }
    } else {
      const el = kanbanScrollRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        const x = st.pointerClientX;
        const distLeft = x - rect.left;
        const distRight = rect.right - x;
        if (distLeft < EDGE) {
          el.scrollLeft -= MAX_SPEED * (1 - Math.max(0, distLeft) / EDGE);
        } else if (distRight < EDGE) {
          el.scrollLeft += MAX_SPEED * (1 - Math.max(0, distRight) / EDGE);
        }
      }
    }

    cardDragRAFRef.current = requestAnimationFrame(runCardDragFrame);
  };

  const handleCardPointerMove = (e) => {
    const st = cardDragStateRef.current;
    if (!st) return;
    // Ignore other pointers while a drag is active (multi-touch)
    if (st.pointerId != null && e.pointerId !== st.pointerId) return;

    const dx = e.clientX - st.startClientX;
    const dy = e.clientY - st.startClientY;

    if (st.pointerType === 'touch') {
      if (!st.isDragReady) {
        // Finger moved before 500ms long-press timer finished
        const dist = Math.hypot(dx, dy);
        if (dist > 10) {
          // Movement exceeds 10px tolerance before timer -> treat as scroll!
          suppressClickRef.current = true;
          stopCardDrag();
        }
        return;
      }

      // Long press duration (~500ms) has completed!
      // Only start drag when movement occurs after hold duration
      if (!st.moved) {
        const moveDist = Math.hypot(dx, dy);
        if (moveDist < 3) return;

        st.moved = true;
        suppressClickRef.current = true;
        if (st.captureEl && st.pointerId != null) {
          try {
            st.captureEl.setPointerCapture(st.pointerId);
          } catch {
            /* older browsers */
          }
        }
        document.body.style.userSelect = 'none';
        document.body.style.touchAction = 'none';
        setDraggedContactId(st.contactId);
        setDragVisual({
          contact: st.contact,
          sourceView: st.sourceView,
          width: st.width,
          height: st.height,
          x: st.x,
          y: st.y,
        });
        cardDragRAFRef.current = requestAnimationFrame(runCardDragFrame);
      }

      // Prevent page/column scroll once dragging with touch
      if (e.cancelable) {
        e.preventDefault();
      }
    } else {
      // Desktop / Mouse: retain existing 5px threshold without delay
      const threshold = 5;
      if (!st.moved && Math.abs(dx) < threshold && Math.abs(dy) < threshold) {
        return;
      }

      if (!st.moved) {
        st.moved = true;
        suppressClickRef.current = true;
        document.body.style.userSelect = 'none';
        document.body.style.touchAction = 'none';
        setDraggedContactId(st.contactId);
        setDragVisual({
          contact: st.contact,
          sourceView: st.sourceView,
          width: st.width,
          height: st.height,
          x: st.x,
          y: st.y,
        });
        cardDragRAFRef.current = requestAnimationFrame(runCardDragFrame);
      }
    }

    st.pointerClientX = e.clientX;
    st.pointerClientY = e.clientY;
    st.x = e.clientX - st.offsetX;
    st.y = e.clientY - st.offsetY;
    applyCardGhostTransform();
  };

  const handleCardPointerUp = (e) => {
    if (cardLongPressTimerRef.current) {
      clearTimeout(cardLongPressTimerRef.current);
      cardLongPressTimerRef.current = null;
    }

    const st = cardDragStateRef.current;
    if (
      st?.pointerId != null &&
      e?.pointerId != null &&
      e.pointerId !== st.pointerId
    )
      return;

    if (st?.moved) {
      const targetStageId = st.overStageId;
      if (targetStageId && String(targetStageId) !== String(st.sourceStageId)) {
        const targetStage = pipelineStages.find(
          (s) => String(s.id) === String(targetStageId),
        );
        if (targetStage?.is_lost || targetStage?.slug === 'lost') {
          setPendingLostMove({
            contactId: st.contactId,
            target_stage_id: targetStage.id,
          });
        } else {
          handleMoveContactMutation.mutate({
            companyId: currentCompanyId,
            contactId: st.contactId,
            payload: { target_stage_id: targetStageId },
          });
        }
      }
    }

    stopCardDrag();
  };

  const handleCardPointerDown = (e, contact, stage) => {
    if (isReorderingMode || e.button !== 0) return;
    // Don't start card drag from nested controls (stage select, links, etc.)
    if (
      e.target.closest(
        'input, select, textarea, a, [data-no-card-drag], [role="listbox"]',
      )
    ) {
      return;
    }
    const nestedButton = e.target.closest('button');
    if (nestedButton && nestedButton !== e.currentTarget) return;

    if (cardLongPressTimerRef.current) {
      clearTimeout(cardLongPressTimerRef.current);
      cardLongPressTimerRef.current = null;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const isTouch = e.pointerType === 'touch';

    cardDragStateRef.current = {
      contact,
      contactId: contact.id,
      sourceStageId: stage.id,
      sourceView: boardView,
      startClientX: e.clientX,
      startClientY: e.clientY,
      pointerClientX: e.clientX,
      pointerClientY: e.clientY,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      width: rect.width,
      height: rect.height,
      x: rect.left,
      y: rect.top,
      frameLastX: rect.left,
      smoothDeltaX: 0,
      tilt: 0,
      moved: false,
      isDragReady: !isTouch,
      overStageId: null,
      pointerId: e.pointerId,
      pointerType: e.pointerType || 'mouse',
      captureEl: e.currentTarget,
    };

    if (!isTouch) {
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* older browsers */
      }
    } else {
      // Touch interaction: start 500ms long-press timer. Do NOT capture pointer yet.
      cardLongPressTimerRef.current = setTimeout(() => {
        const st = cardDragStateRef.current;
        if (!st || String(st.contactId) !== String(contact.id)) return;
        st.isDragReady = true;
        setDragReadyContactId(contact.id);
        try {
          if (typeof navigator !== 'undefined' && navigator.vibrate) {
            navigator.vibrate(40);
          }
        } catch {
          /* older browsers */
        }
      }, 500);
    }

    window.addEventListener('pointermove', handleCardPointerMove, {
      passive: false,
    });
    window.addEventListener('pointerup', handleCardPointerUp);
    window.addEventListener('pointercancel', handleCardPointerUp);
    if (isTouch) {
      window.addEventListener('touchmove', handleCardTouchMove, {
        passive: false,
      });
      window.addEventListener('contextmenu', handleCardContextMenu);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => stopCardDrag(), []);
  const canViewLeads = can(PERMISSIONS.LEADS.VIEW);
  const canViewLeadDetails = can(PERMISSIONS.LEAD_MANAGEMENT.VIEW);
  const canOpenLead = canViewLeads || canViewLeadDetails;
  const canUpdateLeadManagement =
    can(PERMISSIONS.LEAD_MANAGEMENT.UPDATE) || can(PERMISSIONS.LEADS.UPDATE);

  const canCreatePipelines = can(PERMISSIONS.PIPELINES.CREATE);
  const canUpdatePipelines = can(PERMISSIONS.PIPELINES.UPDATE);
  const canMoveLeads = canCreatePipelines || canUpdatePipelines;
  const canUpdateStages = canUpdatePipelines;
  const canOpenPipelineSettings = can(PERMISSIONS.CRM_CONFIG.VIEW);

  const stageOptions = pipelineStages.map((s) => ({
    value: s.id,
    label: typeof s?.name === 'string' ? s.name.trim() : s?.name,
  }));

  // Each entry is one condition the "Filter" menu can add as a chip.
  // `isSet` drives whether the "Clear all" affordance and chip's filled
  // state show; `onClear` resets just that field when its chip is removed.
  const FILTER_FIELDS = [
    {
      key: 'stage',
      label: 'Stage',
      isSet: !!stageFilter,
      onClear: () => setStageFilter(''),
    },
    {
      key: 'source',
      label: 'Source',
      isSet: !!sourceFilter,
      onClear: () => setSourceFilter(''),
    },
    {
      key: 'tag',
      label: 'Tag',
      isSet: !!tagFilter,
      onClear: () => setTagFilter(''),
    },
    {
      key: 'createDate',
      label: 'Create Date',
      isSet: !!(dateFrom || dateTo),
      onClear: () => {
        setDateFrom('');
        setDateTo('');
      },
    },
    {
      key: 'estimatedValue',
      label: 'Estimated Value',
      isSet: !!(valueMin || valueMax),
      onClear: () => {
        setValueMin('');
        setValueMax('');
      },
    },
    {
      key: 'assigned',
      label: 'Assigned Employee',
      isSet: !!assignedFilter,
      onClear: () => setAssignedFilter(''),
    },
    {
      key: 'consent',
      label: 'Consent Status',
      isSet: !!consentFilter,
      onClear: () => setConsentFilter(''),
    },
    {
      key: 'automation',
      label: 'Stage Triggers',
      isSet: !!flowFilter,
      onClear: () => setFlowFilter(''),
    },
    {
      key: 'customField',
      label: 'Custom Field',
      isSet: !!(customFieldSlug && customFieldValue !== ''),
      onClear: () => {
        setCustomFieldSlug('');
        setCustomFieldValue('');
      },
    },
  ];

  const handleAddFilterField = (key) => {
    setActiveFilterFields((prev) =>
      prev.includes(key) ? prev : [...prev, key],
    );
    setIsFilterMenuOpen(false);
  };

  if (isStageModalOpen) {
    return (
      <DashboardPage>
        <CRMStageModal
          isOpen={isStageModalOpen}
          onClose={() => setIsStageModalOpen(false)}
          initialData={stageModalData}
          onSave={handleSaveModalStage}
          isSaving={
            handleCreateStageMutation.isPending ||
            handleUpdateStageMutation.isPending
          }
        />
      </DashboardPage>
    );
  }

  return (
    <DashboardPage
      className={`pipeline-board ${boardView === 'grid' ? '!overflow-hidden !h-full min-h-0' : ''}`}
    >
      <style>
        {`
          @keyframes stage-wiggle {
            0% { transform: rotate(-0.5deg) translateX(-1px); }
            50% { transform: rotate(0.5deg) translateX(1px); }
            100% { transform: rotate(-0.5deg) translateX(-1px); }
          }
          @keyframes fade-in {
            from { opacity: 0; transform: translateY(-5px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fade-in {
            animation: fade-in 0.2s ease-out forwards;
          }
        `}
      </style>

      <div className="flex flex-col gap-3 sm:gap-4 rounded-[16px] bg-white mb-3 sm:mb-4 shrink-0">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 w-full sm:w-auto">
            <SearchInput
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search leads in inquires..."
              className="w-full sm:!w-[20rem] lg:!w-[28rem] max-w-full flex-1 sm:flex-initial min-w-[140px]"
            />
            <div className="flex items-center gap-1 p-1 rounded-[8px] bg-[#F5F5F5] shrink-0">
              <button
                type="button"
                aria-label="List view"
                onClick={() => setBoardView('list')}
                className={`w-8 h-8 rounded-[6px] flex items-center justify-center transition-colors focus:outline-none ${boardView === 'list' ? 'bg-white shadow-sm text-[#50AB3D]' : 'text-[#868686] hover:text-[#1F2241]'}`}
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <line x1="4" y1="6" x2="20" y2="6" />
                  <line x1="4" y1="12" x2="20" y2="12" />
                  <line x1="4" y1="18" x2="20" y2="18" />
                </svg>
              </button>
              <button
                type="button"
                aria-label="Grid view"
                onClick={() => setBoardView('grid')}
                className={`w-8 h-8 rounded-[6px] flex items-center justify-center transition-colors focus:outline-none ${boardView === 'grid' ? 'bg-white shadow-sm text-[#50AB3D]' : 'text-[#868686] hover:text-[#1F2241]'}`}
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <rect x="4" y="4" width="7" height="7" rx="1.5" />
                  <rect x="13" y="4" width="7" height="7" rx="1.5" />
                  <rect x="4" y="13" width="7" height="7" rx="1.5" />
                  <rect x="13" y="13" width="7" height="7" rx="1.5" />
                </svg>
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0 flex-wrap sm:flex-nowrap">
            <div className="relative shrink-0" ref={filterMenuRef}>
              <button
                type="button"
                onClick={() => setIsFilterMenuOpen((prev) => !prev)}
                aria-expanded={isFilterMenuOpen}
                aria-haspopup="menu"
                className={`relative h-10 px-3.5 sm:px-4 rounded-xl text-sm font-semibold flex items-center gap-2 transition-colors shrink-0 ${
                  isFilterMenuOpen || activeFilterFields.length > 0
                    ? 'bg-primary text-white hover:bg-primary-hover'
                    : 'bg-input-overlay text-text-muted'
                }`}
              >
                <FilterIcon className="w-4 h-4" />
                Filter
              </button>
              {isFilterMenuOpen && (
                <div
                  role="menu"
                  data-overlay-container="true"
                  style={{ overscrollBehavior: 'contain' }}
                  className="absolute left-0 top-[46px] z-40 w-56 bg-white border border-[#EBF4EA] rounded-xl shadow-xl py-2"
                >
                  <p className="px-4 py-1.5 text-xs font-semibold text-text-muted uppercase tracking-wide">
                    Filter by
                  </p>
                  {FILTER_FIELDS.filter(
                    (f) => !activeFilterFields.includes(f.key),
                  ).map((f) => (
                    <button
                      key={f.key}
                      type="button"
                      role="menuitem"
                      onClick={() => handleAddFilterField(f.key)}
                      className="w-full text-left px-4 py-2 text-sm font-medium text-[#1F2241] hover:bg-[#EBF4EA] hover:text-[#50AB3D] transition-colors"
                    >
                      {f.label}
                    </button>
                  ))}
                  {activeFilterFields.length === FILTER_FIELDS.length && (
                    <p className="px-4 py-2 text-sm text-text-muted">
                      All filters added
                    </p>
                  )}
                </div>
              )}
            </div>

            {canUpdateStages && (
              <div className="relative shrink-0">
                <Button
                  variant="primary"
                  className={`h-10 px-3.5 sm:px-4 text-sm font-semibold transition-all shrink-0 ${isReorderingMode ? 'shadow-lg ring-2 ring-primary/20' : ''}`}
                  onClick={() => setIsReorderingMode(!isReorderingMode)}
                >
                  {isReorderingMode ? 'Done Reordering' : 'Reorder Columns'}
                </Button>
                {showFirstTimeTooltip && (
                  <div className="absolute top-[50px] right-0 w-64 bg-white border border-[#50AB3D] text-[#1F2241] text-xs p-3 rounded-xl shadow-xl z-50 animate-fade-in">
                    <div className="absolute -top-[5px] right-6 w-2.5 h-2.5 bg-white border-t border-l border-[#50AB3D] transform rotate-45"></div>
                    You can drag inquires stages left or right to change their
                    order.
                  </div>
                )}
              </div>
            )}

            {canOpenPipelineSettings && (
              <button
                type="button"
                onClick={() => navigate('/crm-pipeline/settings')}
                className="h-10 px-3.5 sm:px-4 rounded-xl text-sm font-semibold flex items-center gap-2 bg-input-overlay text-text-muted shrink-0"
              >
                <Settings className="w-4 h-4" />
                Settings
              </button>
            )}
          </div>
        </div>

        {activeFilterFields.length > 0 && (
          <div className="flex flex-wrap items-center gap-3">
            {activeFilterFields.map((key) => {
              const field = FILTER_FIELDS.find((f) => f.key === key);
              if (!field) return null;
              return (
                <div
                  key={key}
                  className="h-10 flex items-center gap-2 pl-3 pr-2 rounded-xl border border-[#EBF4EA] bg-white shrink-0"
                >
                  <span className="text-sm font-semibold text-[#1F2241] whitespace-nowrap">
                    {field.label}
                  </span>
                  <span className="text-sm text-[#868686]">is</span>

                  {key === 'stage' && (
                    <CustomSelect
                      value={stageFilter}
                      onChange={setStageFilter}
                      options={[
                        { value: '', label: 'All Stages' },
                        ...stageOptions,
                      ]}
                      dropdownWidth={180}
                      buttonClassName="h-8 px-2 rounded-lg bg-[#F5F5F5] text-sm font-medium text-[#1F2241] flex items-center gap-1 cursor-pointer focus:outline-none"
                      textSizeClass="text-sm"
                    />
                  )}
                  {key === 'source' && (
                    <CustomSelect
                      value={sourceFilter}
                      onChange={setSourceFilter}
                      options={[
                        { value: '', label: 'All Sources' },
                        ...sourceOptions,
                      ]}
                      dropdownWidth={160}
                      buttonClassName="h-8 px-2 rounded-lg bg-[#F5F5F5] text-sm font-medium text-[#1F2241] flex items-center gap-1 cursor-pointer focus:outline-none"
                      textSizeClass="text-sm"
                    />
                  )}
                  {key === 'tag' && (
                    <CustomSelect
                      value={tagFilter}
                      onChange={setTagFilter}
                      options={[
                        { value: '', label: 'All Tags' },
                        ...tagOptions,
                      ]}
                      searchable
                      onSearchChange={tagsList.setSearch}
                      onLoadMore={tagsList.loadMore}
                      hasMore={tagsList.hasMore}
                      isFetchingMore={tagsList.isFetchingMore}
                      dropdownWidth={160}
                      buttonClassName="h-8 px-2 rounded-lg bg-[#F5F5F5] text-sm font-medium text-[#1F2241] flex items-center gap-1 cursor-pointer focus:outline-none"
                      textSizeClass="text-sm"
                    />
                  )}
                  {key === 'createDate' && (
                    <CustomDateRangePicker
                      startDate={dateFrom}
                      endDate={dateTo}
                      onApply={(start, end) => {
                        setDateFrom(start);
                        setDateTo(end);
                      }}
                      onClear={() => {
                        setDateFrom('');
                        setDateTo('');
                      }}
                      triggerClassName="h-8 px-2 rounded-lg border-0 bg-[#F5F5F5] text-sm justify-between"
                      iconSrc={datePickerCalendarIcon}
                    />
                  )}
                  {key === 'estimatedValue' && (
                    <div className="flex items-center gap-1 h-8 px-2 rounded-lg bg-[#F5F5F5]">
                      <input
                        type="number"
                        value={valueMin}
                        onChange={(e) => setValueMin(e.target.value)}
                        placeholder="Min"
                        className="w-14 text-sm text-[#1F2241] placeholder:text-text-muted bg-transparent border-0 focus:outline-none"
                      />
                      <span className="text-[#868686] text-xs shrink-0">-</span>
                      <input
                        type="number"
                        value={valueMax}
                        onChange={(e) => setValueMax(e.target.value)}
                        placeholder="Max"
                        className="w-14 text-sm text-[#1F2241] placeholder:text-text-muted bg-transparent border-0 focus:outline-none"
                      />
                    </div>
                  )}
                  {key === 'assigned' && (
                    <CustomSelect
                      value={assignedFilter}
                      onChange={setAssignedFilter}
                      options={[
                        { value: '', label: 'All Employees' },
                        ...employeeOptions,
                      ]}
                      searchable
                      onSearchChange={employeesList.setSearch}
                      onLoadMore={employeesList.loadMore}
                      hasMore={employeesList.hasMore}
                      isFetchingMore={employeesList.isFetchingMore}
                      dropdownWidth={200}
                      buttonClassName="h-8 px-2 rounded-lg bg-[#F5F5F5] text-sm font-medium text-[#1F2241] flex items-center gap-1 cursor-pointer focus:outline-none"
                      textSizeClass="text-sm"
                    />
                  )}
                  {key === 'consent' && (
                    <CustomSelect
                      value={consentFilter}
                      onChange={setConsentFilter}
                      options={[
                        { value: '', label: 'All' },
                        { value: 'opt_in', label: 'Opt In' },
                        { value: 'opt_out', label: 'Opt Out' },
                        { value: 'unknown', label: 'Unknown' },
                      ]}
                      dropdownWidth={140}
                      buttonClassName="h-8 px-2 rounded-lg bg-[#F5F5F5] text-sm font-medium text-[#1F2241] flex items-center gap-1 cursor-pointer focus:outline-none"
                      textSizeClass="text-sm"
                    />
                  )}
                  {key === 'automation' && (
                    <CustomSelect
                      value={flowFilter}
                      onChange={setFlowFilter}
                      options={[
                        { value: '', label: 'All Stage Triggers' },
                        ...flowOptions,
                      ]}
                      searchable
                      onSearchChange={flowsList.setSearch}
                      onLoadMore={flowsList.loadMore}
                      hasMore={flowsList.hasMore}
                      isFetchingMore={flowsList.isFetchingMore}
                      dropdownWidth={200}
                      buttonClassName="h-8 px-2 rounded-lg bg-[#F5F5F5] text-sm font-medium text-[#1F2241] flex items-center gap-1 cursor-pointer focus:outline-none"
                      textSizeClass="text-sm"
                    />
                  )}
                  {key === 'customField' && (
                    <div className="flex items-center gap-1">
                      <CustomSelect
                        value={customFieldSlug}
                        onChange={(val) => {
                          setCustomFieldSlug(val);
                          setCustomFieldValue('');
                          setDebouncedCustomFieldValue('');
                        }}
                        options={[
                          { value: '', label: 'Select field' },
                          ...customFieldOptions,
                        ]}
                        searchable
                        onSearchChange={customFieldsList.setSearch}
                        onLoadMore={customFieldsList.loadMore}
                        hasMore={customFieldsList.hasMore}
                        isFetchingMore={customFieldsList.isFetchingMore}
                        buttonClassName="h-8 px-2 rounded-lg bg-[#F5F5F5] text-sm font-medium text-[#1F2241] flex items-center gap-1 cursor-pointer focus:outline-none"
                        textSizeClass="text-sm"
                      />
                      {!customFieldSlug ? (
                        <input
                          type="text"
                          value=""
                          disabled
                          placeholder="Value"
                          className="h-8 w-28 px-2 rounded-lg bg-[#F5F5F5] text-sm text-[#1F2241] placeholder:text-text-muted border-0 focus:outline-none opacity-50 cursor-not-allowed"
                        />
                      ) : selectedCustomField?.field_type === 'select' ? (
                        <CustomSelect
                          value={customFieldValue}
                          onChange={setCustomFieldValue}
                          options={[
                            { value: '', label: 'Select value' },
                            ...(Array.isArray(selectedCustomField.options)
                              ? selectedCustomField.options
                              : String(selectedCustomField.options || '')
                                  .split(',')
                                  .map((o) => o.trim())
                                  .filter(Boolean)
                            ).map((opt) => ({
                              value: opt,
                              label: opt,
                            })),
                          ]}
                          dropdownWidth={140}
                          buttonClassName="h-8 px-2 rounded-lg bg-[#F5F5F5] text-sm font-medium text-[#1F2241] flex items-center gap-1 cursor-pointer focus:outline-none"
                          textSizeClass="text-sm"
                        />
                      ) : selectedCustomField?.field_type === 'boolean' ? (
                        <CustomSelect
                          value={customFieldValue}
                          onChange={setCustomFieldValue}
                          options={[
                            { value: '', label: 'Any' },
                            { value: 'true', label: 'Yes' },
                            { value: 'false', label: 'No' },
                          ]}
                          dropdownWidth={100}
                          buttonClassName="h-8 px-2 rounded-lg bg-[#F5F5F5] text-sm font-medium text-[#1F2241] flex items-center gap-1 cursor-pointer focus:outline-none"
                          textSizeClass="text-sm"
                        />
                      ) : (
                        <input
                          type={
                            selectedCustomField?.field_type === 'number'
                              ? 'number'
                              : selectedCustomField?.field_type === 'date'
                                ? 'date'
                                : 'text'
                          }
                          value={customFieldValue}
                          onChange={(e) => setCustomFieldValue(e.target.value)}
                          placeholder="Value"
                          className="h-8 w-28 px-2 rounded-lg bg-[#F5F5F5] text-sm text-[#1F2241] placeholder:text-text-muted border-0 focus:outline-none"
                        />
                      )}
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => handleRemoveFilterField(key)}
                    aria-label={`Remove ${field.label} filter`}
                    className="text-[#868686] hover:text-[#1F2241] shrink-0"
                  >
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              );
            })}

            {activeFilterFields.length > 0 && (
              <button
                type="button"
                onClick={handleClearAllFilters}
                className="text-sm font-semibold text-[#50AB3D] hover:text-primary-hover shrink-0"
              >
                Clear all
              </button>
            )}
          </div>
        )}
      </div>

      {boardView === 'grid' && (
        <div
          ref={kanbanScrollRef}
          data-pipeline-kanban=""
          onDragOver={(e) => {
            if (isReorderingMode && draggedStageId != null) {
              e.preventDefault();
              stageDragPointerXRef.current = e.clientX;
            }
          }}
          className={`w-full flex-1 min-h-0 overflow-x-auto overflow-y-hidden custom-scrollbar rounded-[16px] bg-[#F5F5F5] p-3 ${
            isReorderingMode ? 'select-none' : ''
          }`}
        >
          <div
            className={`flex h-full gap-4 min-w-max items-stretch ${isReorderingMode ? 'pl-1' : ''}`}
          >
            {isLoading
              ? [...Array(4)].map((_, i) => (
                  <div
                    key={`skeleton-col-${i}`}
                    className="w-[260px] sm:w-[280px] lg:w-[300px] h-full min-h-0 flex flex-col gap-3 shrink-0"
                  >
                    <div className="p-4 bg-white border border-[#EBF4EA] rounded-2xl flex gap-3 items-center shrink-0">
                      <div className="w-4 h-4 rounded-full bg-[#F5F5F5] animate-pulse"></div>
                      <div className="h-5 w-24 bg-[#F5F5F5] rounded animate-pulse"></div>
                    </div>
                    <div className="flex flex-col gap-3">
                      {[...Array(10)].map((_, j) => (
                        <div
                          key={j}
                          className="h-24 bg-white border border-[#EBF4EA] rounded-xl animate-pulse"
                        ></div>
                      ))}
                    </div>
                  </div>
                ))
              : visibleStages.map((stage, index) => {
                  const stagesSource = Array.isArray(boardData)
                    ? boardData
                    : boardData?.stages;
                  const boardStage = Array.isArray(stagesSource)
                    ? stagesSource.find(
                        (s) => String(s.id) === String(stage.id),
                      )
                    : null;
                  let stageContacts =
                    boardStage?.contacts ||
                    boardStage?.leads ||
                    boardStage?.items ||
                    [];
                  const stageCount =
                    boardStage?.count != null
                      ? boardStage.count
                      : stageContacts.length;

                  return (
                    <div
                      key={stage.id}
                      data-stage-dropzone={stage.id}
                      onDragOver={(e) => {
                        if (!isReorderingMode) return;
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                        stageDragPointerXRef.current = e.clientX;
                        if (dragOverStageId !== stage.id) {
                          setDragOverStageId(stage.id);
                        }
                      }}
                      onDragLeave={() => {
                        if (dragOverStageId === stage.id) {
                          setDragOverStageId(null);
                        }
                      }}
                      onDrop={(e) => {
                        if (!isReorderingMode) return;
                        e.preventDefault();
                        e.stopPropagation();
                        setDragOverStageId(null);
                        const sourceId = e.dataTransfer.getData('stageId');
                        if (sourceId) {
                          handleDropColumn(stage.id, sourceId);
                        }
                      }}
                      style={
                        isReorderingMode
                          ? {
                              animation: `stage-wiggle ${0.3 + (index % 3) * 0.05}s infinite ease-in-out`,
                              animationDelay: `${(index % 4) * 0.1}s`,
                            }
                          : {}
                      }
                      className={`w-[260px] sm:w-[280px] lg:w-[340px] h-full min-h-0 flex flex-col max-h-full gap-3 shrink-0 transition-all ${
                        draggedStageId === stage.id
                          ? 'opacity-80 scale-[1.02] z-10'
                          : ''
                      }`}
                    >
                      {/* Column Header — full header is the reorder drag handle */}
                      <div
                        data-stage-drag-handle
                        draggable={isReorderingMode}
                        onDragStart={(e) =>
                          handleStageHeaderDragStart(e, stage)
                        }
                        onDragEnd={handleStageHeaderDragEnd}
                        onPointerDown={(e) =>
                          handleStageHeaderPointerDown(e, stage)
                        }
                        className={`px-3 py-2.5 sm:px-4 sm:py-3 rounded-xl sm:rounded-2xl flex flex-col gap-1.5 sm:gap-2 bg-white border shrink-0 transition-all ${
                          isReorderingMode
                            ? 'border-2 border-dashed border-[#50AB3D]/40 hover:border-primary/60 hover:bg-[#F5F5F5] cursor-grab active:cursor-grabbing select-none touch-none'
                            : 'border-[#EBF4EA]'
                        } ${
                          (dragOverStageId === stage.id &&
                            String(draggedStageId) !== String(stage.id)) ||
                          (cardDropTargetId != null &&
                            String(cardDropTargetId) === String(stage.id))
                            ? 'ring-2 ring-primary shadow-lg border-primary scale-[1.01]'
                            : ''
                        } ${
                          String(draggedStageId) === String(stage.id)
                            ? 'shadow-2xl border-primary/80'
                            : ''
                        }`}
                        style={{
                          ...(isReorderingMode
                            ? { touchAction: 'none', userSelect: 'none' }
                            : {
                                borderBottomWidth: '3px',
                                borderBottomColor: '#50AB3D',
                              }),
                        }}
                      >
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-1.5 sm:gap-2 overflow-hidden pr-1.5">
                            {isReorderingMode && (
                              <GripVertical className="shrink-0 text-[#868686] pointer-events-none w-3.5 h-3.5" />
                            )}
                            <div
                              className="w-2.5 h-2.5 rounded-full shrink-0 pointer-events-none"
                              style={{
                                backgroundColor: stage.color || '#3b82f6',
                              }}
                            />
                            <h3 className="font-bold text-[#1F2241] text-sm sm:text-base truncate pointer-events-none">
                              {typeof stage?.name === 'string'
                                ? stage.name.trim()
                                : stage?.name}
                            </h3>
                            <span
                              className="text-[11px] sm:text-xs font-bold px-1.5 sm:px-2 py-0.5 rounded-full shrink-0 pointer-events-none"
                              style={{
                                backgroundColor: `${stage.color || '#3b82f6'}1A`,
                                color: stage.color || '#3b82f6',
                              }}
                            >
                              {stageCount}
                            </span>
                          </div>
                          {!isReorderingMode && (
                            <button
                              onClick={() => openEditForm(stage)}
                              className="transition-colors p-0.5 sm:p-1 rounded hover:bg-[#F5F5F5] shrink-0"
                            >
                              <img
                                src={editIcon}
                                alt="Edit"
                                className="w-3.5 h-3.5 sm:w-4 sm:h-4"
                              />
                            </button>
                          )}
                        </div>
                        <div className="flex items-center justify-between text-[11px] sm:text-xs pointer-events-none">
                          <span className="text-[#868686] font-medium">
                            Total Value
                          </span>
                          <span className="text-[#1F2241] font-bold tracking-wide">
                            {formatCurrency(boardStage?.total_estimated_value)}
                          </span>
                        </div>
                      </div>

                      {/* Column Body (Cards) */}
                      <div
                        id={`stage-column-scroll-${stage.id}`}
                        onScroll={(e) => handleStageColumnScroll(e, stage)}
                        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden stage-column-scrollbar flex flex-col gap-3 pr-0.5"
                      >
                        {stageContacts.length === 0 ? (
                          <div className="flex-1 flex items-center justify-center min-h-[100px] border-2 border-dashed border-[#EBF4EA] rounded-xl pointer-events-none">
                            <p className="text-sm text-[#868686] font-medium">
                              {isReorderingMode
                                ? '↔ Drag stage to reorder'
                                : ''}
                            </p>
                          </div>
                        ) : (
                          <>
                            {stageContacts.map((contact) => {
                              const isNewContact = contact?.is_new === true;
                              return (
                                <div
                                  id={`contact-card-${contact.id}`}
                                  key={contact.id}
                                  onPointerDown={(e) =>
                                    handleCardPointerDown(e, contact, stage)
                                  }
                                  onClick={() => {
                                    if (suppressClickRef.current) {
                                      suppressClickRef.current = false;
                                      return;
                                    }
                                    if (!isReorderingMode && canOpenLead) {
                                      handleViewContact(contact, stage);
                                    }
                                  }}
                                  style={{
                                    touchAction: 'manipulation',
                                    WebkitTouchCallout: 'none',
                                    userSelect: 'none',
                                  }}
                                  className={`relative bg-white border rounded-xl p-4 transition-all group shadow-sm select-none ${
                                    !isReorderingMode
                                      ? 'cursor-grab active:cursor-grabbing hover:border-primary'
                                      : 'opacity-70 pointer-events-none'
                                  } ${
                                    draggedContactId === contact.id
                                      ? 'opacity-50 border-primary ring-1 ring-primary'
                                      : dragReadyContactId === contact.id
                                        ? 'ring-2 ring-primary border-primary scale-[1.02] shadow-md'
                                        : highlightedContactId === contact.id
                                          ? 'border-2 border-[#50AB3D] bg-[#EBF4EA]/60 shadow-lg animate-notification-pulse'
                                          : 'border-[#EBF4EA]'
                                  }`}
                                >
                                  <ContactCardBody
                                    contact={contact}
                                    isNewContact={isNewContact}
                                    availableTags={tagsData}
                                    onTagChange={handleChangeContactTag}
                                    pipelineStages={pipelineStages}
                                    currentStageId={stage.id}
                                    onStageChange={handleChangeContactStage}
                                    companyId={currentCompanyId}
                                    canAssignEmployee={canUpdateLeadManagement}
                                    employeesData={employeesData}
                                  />
                                </div>
                              );
                            })}

                            <StageSentinel
                              onVisible={() => fetchNextPageForStage(stage)}
                              hasMore={
                                stagePaginationRef.current[stage.id]
                                  ?.hasMore !== undefined
                                  ? stagePaginationRef.current[stage.id]
                                      ?.hasMore
                                  : (stage.count != null &&
                                      stageContacts.length < stage.count) ||
                                    !!boardResponse?.next
                              }
                              isLoading={!!loadingStages[stage.id]}
                            />
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}

            {/* Add Stage Placeholder */}
            <div
              onClick={openCreateForm}
              className={`w-[260px] sm:w-[280px] lg:w-[300px] h-[62px] sm:h-[70px] self-start flex items-center justify-center bg-white border-2 border-dashed transition-colors rounded-xl sm:rounded-2xl shrink-0 ${isReorderingMode ? 'border-[#EBF4EA] text-[#868686]/30 cursor-default pointer-events-none' : 'border-[#EBF4EA] hover:border-primary cursor-pointer'}`}
            >
              <span
                className={`text-sm font-medium transition-colors flex items-center gap-2 ${isReorderingMode ? 'text-[#868686]/30' : 'text-[#868686] hover:text-primary'}`}
              >
                Add Stage <PlusIcon className="w-4 h-4" />
              </span>
            </div>
          </div>
        </div>
      )}

      {boardView === 'list' && (
        <div
          className="w-full flex flex-col gap-4"
          onDragOver={(e) => {
            if (isReorderingMode) {
              stageDragPointerYRef.current = e.clientY;
            }
          }}
        >
          {isLoading
            ? [...Array(3)].map((_, i) => (
                <div
                  key={`skeleton-list-${i}`}
                  className="rounded-2xl bg-white border border-[#EBF4EA] overflow-hidden"
                >
                  <div className="p-4 flex gap-3 items-center">
                    <div className="w-4 h-4 rounded-full bg-[#F5F5F5] animate-pulse"></div>
                    <div className="h-5 w-32 bg-[#F5F5F5] rounded animate-pulse"></div>
                  </div>
                  <div className="p-3 flex flex-col gap-2">
                    {[...Array(10)].map((_, j) => (
                      <div
                        key={j}
                        className="h-12 bg-[#F5F5F5] rounded-lg animate-pulse"
                      ></div>
                    ))}
                  </div>
                </div>
              ))
            : visibleStages.map((stage, index) => {
                const stagesSource = Array.isArray(boardData)
                  ? boardData
                  : boardData?.stages;
                const boardStage = Array.isArray(stagesSource)
                  ? stagesSource.find((s) => String(s.id) === String(stage.id))
                  : null;
                const stageContacts =
                  boardStage?.contacts ||
                  boardStage?.leads ||
                  boardStage?.items ||
                  [];
                const stageCount =
                  boardStage?.count != null
                    ? boardStage.count
                    : stageContacts.length;
                const color = stage.color || '#3b82f6';
                const isCollapsed = !!collapsedStages[stage.id];

                return (
                  <div
                    key={stage.id}
                    data-stage-dropzone={stage.id}
                    onDragOver={(e) => {
                      if (!isReorderingMode) return;
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                      stageDragPointerYRef.current = e.clientY;
                      if (String(dragOverStageId) !== String(stage.id)) {
                        setDragOverStageId(stage.id);
                      }
                    }}
                    onDragLeave={() => {
                      if (String(dragOverStageId) === String(stage.id)) {
                        setDragOverStageId(null);
                      }
                    }}
                    onDrop={(e) => {
                      if (!isReorderingMode) return;
                      e.preventDefault();
                      e.stopPropagation();
                      setDragOverStageId(null);
                      const sourceId = e.dataTransfer.getData('stageId');
                      if (sourceId) {
                        handleDropColumn(stage.id, sourceId);
                      }
                    }}
                    className={`rounded-2xl p-3 transition-all ${
                      String(draggedStageId) === String(stage.id)
                        ? 'opacity-60 scale-[0.99]'
                        : ''
                    } ${
                      (String(dragOverStageId) === String(stage.id) &&
                        String(draggedStageId) !== String(stage.id)) ||
                      (cardDropTargetId != null &&
                        String(cardDropTargetId) === String(stage.id))
                        ? 'ring-2 ring-primary'
                        : ''
                    }`}
                    style={{
                      backgroundColor: `${color}14`,
                      ...(isReorderingMode
                        ? {
                            animation: `stage-wiggle ${0.3 + (index % 3) * 0.05}s infinite ease-in-out`,
                            animationDelay: `${(index % 4) * 0.1}s`,
                          }
                        : {}),
                    }}
                  >
                    {/* Below sm, Total Value moves to its own line above the
                      table instead of squeezing into the header row — the
                      inline reserved-column version further down is hidden
                      at that width. */}
                    <div className="sm:hidden px-1 pb-2 text-sm text-[#1F2241] font-medium">
                      Total Value :{' '}
                      <span className="font-bold">
                        {formatCurrency(boardStage?.total_estimated_value)}
                      </span>
                    </div>

                    <div className="overflow-x-auto custom-scrollbar">
                      <div style={{ minWidth: LIST_MIN_WIDTH }}>
                        <div
                          role="button"
                          tabIndex={0}
                          data-stage-drag-handle
                          draggable={isReorderingMode}
                          onDragStart={(e) =>
                            handleStageHeaderDragStart(e, stage)
                          }
                          onDragEnd={handleStageHeaderDragEnd}
                          onPointerDown={(e) =>
                            handleStageHeaderPointerDown(e, stage)
                          }
                          onClick={() => {
                            if (suppressClickRef.current) {
                              suppressClickRef.current = false;
                              return;
                            }
                            if (isReorderingMode) return;
                            toggleStageCollapsed(stage.id);
                          }}
                          onKeyDown={(e) => {
                            if (isReorderingMode) return;
                            if (e.key === 'Enter' || e.key === ' ') {
                              toggleStageCollapsed(stage.id);
                            }
                          }}
                          className={`w-full flex items-center gap-4 px-4 py-2.5 text-left focus:outline-none ${
                            isReorderingMode
                              ? 'cursor-grab active:cursor-grabbing select-none touch-none'
                              : 'cursor-pointer'
                          }`}
                          style={
                            isReorderingMode
                              ? { touchAction: 'none', userSelect: 'none' }
                              : undefined
                          }
                        >
                          <div
                            className="flex items-center gap-3 min-w-0 flex-1"
                            style={{ minWidth: LIST_COL_WIDTH.name }}
                          >
                            {isReorderingMode ? (
                              <GripVertical className="w-4 h-4 shrink-0 text-[#868686] pointer-events-none" />
                            ) : (
                              <svg
                                className={`w-4 h-4 text-[#868686] shrink-0 transition-transform ${isCollapsed ? '-rotate-90' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                                />
                              </svg>
                            )}
                            <div
                              className="w-3 h-3 rounded-full shrink-0 pointer-events-none"
                              style={{ backgroundColor: color }}
                            />
                            <span className="font-bold text-[#1F2241] text-base truncate pointer-events-none">
                              {typeof stage?.name === 'string'
                                ? stage.name.trim()
                                : stage?.name}
                            </span>
                            <span
                              className="text-xs font-bold px-2 py-0.5 rounded-full shrink-0 bg-white pointer-events-none"
                              style={{ color }}
                            >
                              {stageCount}
                            </span>
                            {!isReorderingMode && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEditForm(stage);
                                }}
                                className="p-1 rounded hover:bg-white/60 transition-colors shrink-0 focus:outline-none"
                              >
                                <img
                                  src={editIcon}
                                  alt="Edit"
                                  className="w-4 h-4"
                                />
                              </button>
                            )}
                          </div>

                          {/* Desktop only — a real fixed-width column, matched
                          by an equal-width spacer in every data row below,
                          so Tag/Date/Assigned/Primary Source/Amount always
                          land at the same X regardless of the currency
                          value's length. On mobile this is hidden in favor
                          of the stacked line above the table. */}
                          <span
                            className="hidden sm:block text-sm text-[#1F2241] font-medium whitespace-nowrap shrink-0 pointer-events-none"
                            style={{ width: LIST_COL_WIDTH.totalValue }}
                          >
                            Total Value :{' '}
                            <span className="font-bold">
                              {formatCurrency(
                                boardStage?.total_estimated_value,
                              )}
                            </span>
                          </span>
                          <span
                            className="text-sm font-semibold text-[#868686] shrink-0 pointer-events-none"
                            style={{ width: LIST_COL_WIDTH.tag }}
                          >
                            Tag
                          </span>
                          <span
                            className="text-sm font-semibold text-[#868686] shrink-0 pointer-events-none"
                            style={{ width: LIST_COL_WIDTH.date }}
                          >
                            Date
                          </span>
                          <span
                            className="text-sm font-semibold text-[#868686] shrink-0 pointer-events-none"
                            style={{ width: LIST_COL_WIDTH.assigned }}
                          >
                            Assigned
                          </span>
                          <span
                            className="text-sm font-semibold text-[#868686] shrink-0 pointer-events-none"
                            style={{ width: LIST_COL_WIDTH.source }}
                          >
                            Primary Source
                          </span>
                          <span
                            className="text-sm font-semibold text-[#868686] text-right shrink-0"
                            style={{ width: LIST_COL_WIDTH.amount }}
                          >
                            Amount
                          </span>
                        </div>

                        <div
                          className="grid transition-[grid-template-rows] duration-300 ease-in-out"
                          style={{
                            gridTemplateRows: isCollapsed ? '0fr' : '1fr',
                          }}
                        >
                          <div className="overflow-hidden min-h-0">
                            <div className="rounded-xl bg-white overflow-hidden flex flex-col">
                              {stageContacts.length === 0 ? (
                                <div className="p-6 text-center text-sm text-[#868686] font-medium">
                                  {/* Empty space */}
                                </div>
                              ) : (
                                <>
                                  {stageContacts.map((contact) => (
                                    <button
                                      type="button"
                                      key={contact.id}
                                      onPointerDown={(e) =>
                                        handleCardPointerDown(e, contact, stage)
                                      }
                                      onClick={() => {
                                        if (suppressClickRef.current) {
                                          suppressClickRef.current = false;
                                          return;
                                        }
                                        handleViewContact(contact, stage);
                                      }}
                                      id={`contact-card-list-${contact.id}`}
                                      style={{
                                        touchAction: 'manipulation',
                                        WebkitTouchCallout: 'none',
                                        userSelect: 'none',
                                      }}
                                      className={`w-full flex items-center gap-4 px-4 py-2.5 border-t text-left transition-all cursor-grab active:cursor-grabbing select-none ${
                                        draggedContactId === contact.id
                                          ? 'opacity-50 border-primary ring-1 ring-primary'
                                          : dragReadyContactId === contact.id
                                            ? 'ring-2 ring-primary border-primary bg-[#EBF4EA]/40'
                                            : highlightedContactId ===
                                                contact.id
                                              ? 'bg-[#EBF4EA]/70 border-[#50AB3D] border-l-4 animate-notification-pulse'
                                              : 'border-[#EBF4EA] hover:bg-[#F5F5F5]'
                                      }`}
                                    >
                                      <div
                                        className="min-w-0 flex-1"
                                        style={{
                                          minWidth: LIST_COL_WIDTH.name,
                                        }}
                                      >
                                        <p className="font-bold text-[#1F2241] text-sm truncate">
                                          {contact.name ||
                                            contact.contact?.name ||
                                            'Unknown'}
                                        </p>
                                        <p className="text-xs text-[#868686] truncate">
                                          {getDisplayPhone(contact)}
                                        </p>
                                      </div>

                                      {/* Matches the header's desktop-only Total
                                    Value column so Tag onward keep lining up
                                    with it at sm+. */}
                                      <span
                                        className="hidden sm:block shrink-0"
                                        style={{
                                          width: LIST_COL_WIDTH.totalValue,
                                        }}
                                      />

                                      <div
                                        className="min-w-0 shrink-0"
                                        style={{ width: LIST_COL_WIDTH.tag }}
                                      >
                                        {Array.isArray(contact.tags) &&
                                          contact.tags.length > 0 && (
                                            <PipelineTagChip
                                              tag={contact.tags[0]}
                                              className="max-w-full"
                                            />
                                          )}
                                      </div>

                                      <div
                                        className="min-w-0 shrink-0"
                                        style={{ width: LIST_COL_WIDTH.date }}
                                      >
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs text-[#868686] bg-[#F5F5F5] truncate">
                                          <img
                                            src={calendarIcon}
                                            alt=""
                                            className="w-3.5 h-3.5 object-contain shrink-0"
                                          />
                                          <span className="truncate font-medium">
                                            {contact.created_at
                                              ? formatCardDate(
                                                  contact.created_at,
                                                )
                                              : '—'}
                                          </span>
                                        </span>
                                      </div>

                                      <div
                                        className="flex items-center gap-1.5 min-w-0 shrink-0"
                                        style={{
                                          width: LIST_COL_WIDTH.assigned,
                                        }}
                                      >
                                        {(() => {
                                          const listAssignedId =
                                            contact.assigned_to_employee_id ||
                                            contact.assigned_to_id ||
                                            contact.assignee_id ||
                                            contact.assignee?.id;
                                          const listMatchedEmp = (
                                            employeesData || []
                                          ).find(
                                            (emp) =>
                                              (listAssignedId &&
                                                String(emp.id) ===
                                                  String(listAssignedId)) ||
                                              (contact.assigned_to_name &&
                                                (String(emp.id) ===
                                                  String(
                                                    contact.assigned_to_name,
                                                  ) ||
                                                  String(emp.phone) ===
                                                    String(
                                                      contact.assigned_to_name,
                                                    ))),
                                          );
                                          const rawListName =
                                            contact.assigned_to_employee_name ||
                                            (contact.assigned_to_name &&
                                            contact.assigned_to_name !==
                                              'Unassigned'
                                              ? contact.assigned_to_name
                                              : null) ||
                                            contact.assignee?.name;
                                          const resolvedListName =
                                            (rawListName &&
                                            !/^\d+$/.test(
                                              String(rawListName).trim(),
                                            )
                                              ? rawListName
                                              : null) ||
                                            listMatchedEmp?.name ||
                                            listMatchedEmp?.full_name ||
                                            rawListName ||
                                            '';
                                          return (
                                            <InlineEmployeeSelector
                                              variant="list"
                                              companyId={currentCompanyId}
                                              contactId={contact.id}
                                              employeeId={listAssignedId}
                                              employeeName={resolvedListName}
                                              disabled={
                                                !canUpdateLeadManagement
                                              }
                                            />
                                          );
                                        })()}
                                      </div>

                                      <div
                                        className="min-w-0 shrink-0"
                                        style={{ width: LIST_COL_WIDTH.source }}
                                      >
                                        {Array.isArray(contact.sources) &&
                                          contact.sources.length > 0 && (
                                            <div className="flex items-center gap-1.5 min-w-0">
                                              <SourceIcon
                                                type={contact.sources[0]}
                                              />
                                              <span className="truncate text-sm font-medium text-[#1F2241] capitalize">
                                                {contact.sources[0]}
                                              </span>
                                            </div>
                                          )}
                                      </div>

                                      <div
                                        className="text-right shrink-0"
                                        style={{ width: LIST_COL_WIDTH.amount }}
                                      >
                                        {contact.estimated_value !== null &&
                                          contact.estimated_value !==
                                            undefined &&
                                          contact.estimated_value !== '' && (
                                            <span className="inline-block text-xs font-bold text-[#50AB3D] bg-[#EBF4EA] border border-[#50AB3D]/30 px-2.5 py-0.5 rounded-full">
                                              {formatCurrency(
                                                contact.estimated_value,
                                              )}
                                            </span>
                                          )}
                                      </div>
                                    </button>
                                  ))}

                                  <StageSentinel
                                    onVisible={() =>
                                      fetchNextPageForStage(stage)
                                    }
                                    hasMore={
                                      stagePaginationRef.current[stage.id]
                                        ?.hasMore !== undefined
                                        ? stagePaginationRef.current[stage.id]
                                            ?.hasMore
                                        : (stage.count != null &&
                                            stageContacts.length <
                                              stage.count) ||
                                          !!boardResponse?.next
                                    }
                                    isLoading={!!loadingStages[stage.id]}
                                  />
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

          {canCreatePipelines && (
            <div
              onClick={openCreateForm}
              className="w-full flex items-center justify-center h-[64px] bg-white border-2 border-dashed border-[#EBF4EA] hover:border-primary transition-colors rounded-2xl cursor-pointer"
            >
              <span className="text-sm font-medium text-[#868686] hover:text-primary transition-colors flex items-center gap-2">
                Add Stage <PlusIcon className="w-4 h-4" />
              </span>
            </div>
          )}
        </div>
      )}
      {dragVisual &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={cardGhostElRef}
            className="fixed top-0 left-0 z-[999] pointer-events-none will-change-transform"
            style={{
              width: dragVisual.width,
              transform: `translate3d(${dragVisual.x}px, ${dragVisual.y}px, 0) rotate(0deg)`,
            }}
          >
            {dragVisual.sourceView === 'list' ? (
              <div
                className="flex items-center gap-4 px-4 bg-white border border-primary rounded-lg shadow-2xl overflow-hidden"
                style={{ height: dragVisual.height }}
              >
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-[#1F2241] text-sm truncate">
                    {dragVisual.contact?.name || 'Unknown'}
                  </p>
                  <p className="text-xs text-[#868686] truncate">
                    {getDisplayPhone(dragVisual.contact)}
                  </p>
                </div>
                <div
                  className="hidden sm:block shrink-0"
                  style={{ width: LIST_COL_WIDTH.totalValue }}
                />
                <div
                  className="min-w-0 shrink-0"
                  style={{ width: LIST_COL_WIDTH.tag }}
                >
                  {Array.isArray(dragVisual.contact?.tags) &&
                    dragVisual.contact.tags.length > 0 && (
                      <PipelineTagChip
                        tag={dragVisual.contact.tags[0]}
                        className="max-w-full"
                      />
                    )}
                </div>
                <div
                  className="min-w-0 shrink-0"
                  style={{ width: LIST_COL_WIDTH.date }}
                >
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs text-[#868686] bg-[#F5F5F5] truncate">
                    <img
                      src={calendarIcon}
                      alt=""
                      className="w-3.5 h-3.5 object-contain shrink-0"
                    />
                    <span className="truncate font-medium">
                      {dragVisual.contact?.created_at
                        ? formatCardDate(dragVisual.contact.created_at)
                        : '—'}
                    </span>
                  </span>
                </div>
                <div
                  className="flex items-center gap-1.5 min-w-0 shrink-0"
                  style={{ width: LIST_COL_WIDTH.assigned }}
                >
                  <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                    <span className="text-[10px] font-bold text-primary">
                      {(
                        dragVisual.contact?.assigned_to_employee_name ||
                        dragVisual.contact?.assigned_to_name ||
                        'U'
                      )
                        .charAt(0)
                        .toUpperCase()}
                    </span>
                  </div>
                  <span className="truncate text-sm font-medium text-[#1F2241]">
                    {dragVisual.contact?.assigned_to_employee_name ||
                      dragVisual.contact?.assigned_to_name ||
                      'Unassigned'}
                  </span>
                </div>
                <div
                  className="min-w-0 shrink-0"
                  style={{ width: LIST_COL_WIDTH.source }}
                >
                  {Array.isArray(dragVisual.contact?.sources) &&
                    dragVisual.contact.sources.length > 0 && (
                      <div className="flex items-center gap-1.5 min-w-0">
                        <SourceIcon type={dragVisual.contact.sources[0]} />
                        <span className="truncate text-sm font-medium text-[#1F2241] capitalize">
                          {dragVisual.contact.sources[0]}
                        </span>
                      </div>
                    )}
                </div>
                <div
                  className="text-right shrink-0"
                  style={{ width: LIST_COL_WIDTH.amount }}
                >
                  {dragVisual.contact?.estimated_value !== null &&
                    dragVisual.contact?.estimated_value !== undefined &&
                    dragVisual.contact?.estimated_value !== '' && (
                      <span className="inline-block text-xs font-bold text-[#50AB3D] bg-[#EBF4EA] border border-[#50AB3D]/30 px-2.5 py-0.5 rounded-full">
                        {formatCurrency(dragVisual.contact.estimated_value)}
                      </span>
                    )}
                </div>
              </div>
            ) : (
              <div className="relative bg-white border border-primary rounded-xl p-4 shadow-2xl scale-[1.03] opacity-95">
                <ContactCardBody
                  contact={dragVisual.contact}
                  isNewContact={dragVisual.contact?.is_new === true}
                  companyId={currentCompanyId}
                  canAssignEmployee={false}
                  employeesData={employeesData}
                />
              </div>
            )}
          </div>,
          document.body,
        )}

      <CRMLostReasonModal
        isOpen={!!pendingLostMove}
        onClose={() => setPendingLostMove(null)}
        onSubmit={(reason) => {
          if (pendingLostMove) {
            handleMoveContactMutation.mutate({
              companyId: currentCompanyId,
              contactId: pendingLostMove.contactId,
              payload: {
                target_stage_id: pendingLostMove.target_stage_id,
                lost_reason: reason,
              },
            });
          }
        }}
        isPending={handleMoveContactMutation.isPending}
      />
    </DashboardPage>
  );
}
